import {
  DEFAULT_DELAY_QUERIES as DEFAULT_FORGETTING_CURVE_DELAY_QUERIES,
  buildDeckSummaryIndex,
  buildDelayFeatures,
  getEffectiveInferenceHistoryWindow,
  getReviewCountSaturationMax,
  buildSequenceInputs,
  buildStateFeatures,
  extendDeckSummary,
  predictForgettingCurveBatch,
  predictRecallCurvesForCards,
} from "./onnxForgettingCurveCore";
import { normalizeIsotonicCalibration } from "./isotonicCalibration";
import loadForgettingCurveSession from "./onnxForgettingCurveSession";
import {
  advanceFsrsState,
  DEFAULT_ENABLE_FUZZ,
  DEFAULT_ENABLE_SHORT_TERM,
  DEFAULT_FSRS_PARAMETERS,
  DEFAULT_LEARNING_STEPS_MINUTES,
  DEFAULT_PLANNER_TARGET,
  DEFAULT_RELEARNING_STEPS_MINUTES,
  normalizeFsrsParameters,
  normalizeBooleanFlag,
  normalizePlannerTarget,
  normalizeSchedulerModel,
  projectFsrsStateAtTime,
  replayFsrsHistory,
  normalizeStepMinutes,
  optimizeFsrsParameters,
} from "./fsrsScheduler";
import { computeEasyThresholdSecondsFromReviews } from "./fsrsReviewRating";

const TEN_MINUTE_RECALL_TARGET = 0.95;
const TEN_MINUTE_DELAY_QUERY = { label: "10m", hours: 1 / 6, reviews: 0 };
const ONE_HOUR_DELAY_QUERY = { label: "1h", hours: 1, reviews: 0 };
const ONE_DAY_DELAY_QUERY = { label: "1d", hours: 24, reviews: 0 };
const THREE_DAY_DELAY_QUERY = { label: "3d", hours: 24 * 3, reviews: 0 };
const DEFAULT_PLANNING_HORIZON_MINUTES = 60;
const DEFAULT_PLANNING_THRESHOLD = 0.75;
const DEFAULT_PLANNING_UTILITY_SHARPNESS = 24;
const DEFAULT_PLANNER_BEAM_DEPTH = 2;
const DEFAULT_PLANNER_BEAM_WIDTH = 4;
const DEFAULT_PLANNER_BEAM_ROOT_WIDTH = 10;
const DEFAULT_PLANNER_EVAL_CARD_COUNT = 12;
const DEFAULT_PLANNER_BEAM_RANDOM_FRACTION = 0.3;
const DEFAULT_MIN_UNDER_95_COUNT = 2;
const DEFAULT_REVIEW_RATE_PER_HOUR = 40;
const REVIEW_RATE_WINDOW = 80;
const DELAYED_GAIN_RECENT_REVIEW_WINDOW_DAYS = 5;
const DELAYED_GAIN_MANY_REVIEW_MIN_TOTAL_REVIEWS = 20;
const FSRS_NEW_SIBLING_DELAY_SECONDS = 60 * 60;
const DELAYED_GAIN_RECENT_REVIEW_HOURS = 3;
const DELAYED_GAIN_POST_REVIEW_HORIZON_HOURS = 1;
const DELAYED_GAIN_CANDIDATE_DELAYS_MINUTES = [1440, 4320];
const HIGH_MATURITY_POST_REVIEW_DELAY_QUERIES = [
  { label: "1h", hours: 1 },
  { label: "2h", hours: 2 },
  { label: "4h", hours: 4 },
  { label: "1d", hours: 24 },
  { label: "2d", hours: 48 },
  { label: "3d", hours: 72 },
  { label: "4d", hours: 96 },
  { label: "5d", hours: 120 },
  { label: "6d", hours: 144 },
  { label: "7d", hours: 168 },
  { label: "14d", hours: 336 },
  { label: "30d", hours: 720 },
];
const DUE_NOW_TOLERANCE_MINUTES = 0.5;
const STABLE_CARD_REINFERENCE_INTERVAL = 10;
const STABLE_CARD_REINFERENCE_MIN_REVIEWS = 10;
const STABLE_CARD_REINFERENCE_MIN_PNOW = 0.99;
const RANDOM_SEEN_CARD_PROBABILITY = 0.08;
const LOWEST_PROBABILITY_CARD_SELECTION_PROBABILITY = 0.1;
const AUDIT_RANDOM_SEEN_SELECTION_PROBABILITY = 0.1;
const EXPLORATION_SUPPRESSION_FRACTION = 0.4;
const EXPLORATION_RANDOM_SEEN_SELECTION_PROBABILITY = 0.2;
const EXPLORATION_DELAY_MIN_SECONDS = 5 * 60;
const EXPLORATION_DELAY_MAX_SECONDS = 70 * 60;
const NEVER_DUE_LABEL = "never";
const SESSION_REVIEW_CACHE_LIMIT = 10;
const SESSION_SIDELINE_MIN_CORRECT_REVIEWS = 3;
const PLANNING_DELAY_MINUTES = [
  0,
  0.05,
  0.16,
  0.5,
  1,
  2,
  5,
  10,
  15,
  20,
  30,
  45,
  60,
  120,
  240,
  720,
  1440,
  2880,
  4320,
  5760,
  7200,
  8640,
  10080,
  43200,
];

const FORGETTING_CURVE_METADATA_URL = `${process.env.PUBLIC_URL}/recall_forgetting_curve_model.metadata.json`;
const FORGETTING_CURVE_CALIBRATION_URL = `${process.env.PUBLIC_URL}/recall_forgetting_curve_model.calibration.json`;
const TIMING_DIAGNOSTIC_BUCKETS = [
  { label: "<10m", maxMinutes: 10 },
  { label: "10m-1h", maxMinutes: 60 },
  { label: "1h-6h", maxMinutes: 360 },
  { label: "6h-1d", maxMinutes: 1440 },
  { label: "1d-3d", maxMinutes: 4320 },
  { label: "3d-7d", maxMinutes: 10080 },
  { label: "7d-30d", maxMinutes: 43200 },
  { label: ">30d", maxMinutes: Number.POSITIVE_INFINITY },
];

const clampProbability = (value) => {
  if (!Number.isFinite(value)) {
    return 0;
  }

  if (value <= 0) {
    return 0;
  }

  if (value >= 1) {
    return 1;
  }

  return value;
};

const getSearchParams = () => {
  try {
    return new URLSearchParams(window.location.search);
  } catch (error) {
    return new URLSearchParams();
  }
};

const hasTruthyQueryValue = (value) =>
  value != null &&
  ["1", "true", "yes", "on"].includes(String(value).trim().toLowerCase());

const getStableCardReinferenceBucket = (cardId) =>
  Math.abs(Number(cardId)) % STABLE_CARD_REINFERENCE_INTERVAL;

const shouldReuseStableCardPlan = ({
  reviewStep,
  cardId,
  historyRows,
  cachedPlan,
}) => {
  if (!cachedPlan || !Number.isFinite(Number(cachedPlan.pNow))) {
    return false;
  }

  if ((historyRows?.length ?? 0) <= STABLE_CARD_REINFERENCE_MIN_REVIEWS) {
    return false;
  }

  if (Number(cachedPlan.pNow) < STABLE_CARD_REINFERENCE_MIN_PNOW) {
    return false;
  }

  return (
    Math.abs(Number(reviewStep)) % STABLE_CARD_REINFERENCE_INTERVAL !==
    getStableCardReinferenceBucket(cardId)
  );
};

const getCachedGreedyScoreFromPlan = (plan) => {
  if (!plan) {
    return null;
  }

  return {
    pCorrect: clampProbability(plan.pAttemptAtBest ?? plan.pNow ?? 0),
    pIfCorrectAtHorizon: clampProbability(
      plan.candidates?.[0]?.pIfCorrectAtHorizon ?? plan.pOneHour ?? plan.pNow ?? 0
    ),
    pIfIncorrectAtHorizon: clampProbability(
      plan.candidates?.[0]?.pIfIncorrectAtHorizon ?? plan.pOneHour ?? plan.pNow ?? 0
    ),
    expectedHorizonProbability: clampProbability(
      plan.expectedHorizonProbability ?? plan.pOneHour ?? plan.pNow ?? 0
    ),
  };
};

const getFlatGreedyScoreFromProbability = (probability) => {
  const p = clampProbability(probability ?? 0);
  return {
    pCorrect: p,
    pIfCorrectAtHorizon: p,
    pIfIncorrectAtHorizon: p,
    expectedHorizonProbability: p,
  };
};

export const getFutureInferenceThreshold = (settings = {}) =>
  Math.max(
    TEN_MINUTE_RECALL_TARGET,
    clampProbability(settings?.threshold ?? DEFAULT_PLANNING_THRESHOLD)
  );

const shouldProfileSubmitPath = (searchParams = getSearchParams()) =>
  hasTruthyQueryValue(searchParams.get("profile_submit"));

const createTimingProfiler = (enabled, label) => {
  if (!enabled || typeof performance === "undefined") {
    return {
      mark() {},
      flush() {},
    };
  }

  const startedAt = performance.now();
  let lastMarkAt = startedAt;
  const rows = [];

  return {
    mark(step, extra = {}) {
      const now = performance.now();
      rows.push({
        step,
        ms_since_last: Number((now - lastMarkAt).toFixed(2)),
        ms_total: Number((now - startedAt).toFixed(2)),
        ...extra,
      });
      lastMarkAt = now;
    },
    flush(extra = {}) {
      console.groupCollapsed(`[selector-profile] ${label}`);
      console.table(rows);
      console.log(extra);
      console.groupEnd();
    },
  };
};

export const getRandomizationSettings = (searchParams = getSearchParams()) => ({
  randomize: clampProbability(Number(searchParams.get("randomize"))),
  randomizeNew: clampProbability(Number(searchParams.get("randomize_new"))),
  randomizeRecent: clampProbability(Number(searchParams.get("randomize_recent"))),
});

export const getExplorationSettings = (searchParams = getSearchParams()) => ({
  enabled: hasTruthyQueryValue(searchParams.get("exploration_mode")),
});

export const getPlanningSettings = (
  searchParams = getSearchParams(),
  params = {}
) => {
  const beamRandomFractionParam = searchParams.get(
    "planner_beam_random_fraction"
  );
  const horizonMinutes = Math.max(
    5,
    Number(searchParams.get("planner_horizon_minutes")) ||
      DEFAULT_PLANNING_HORIZON_MINUTES
  );
  const threshold = clampProbability(
    Number(searchParams.get("planner_threshold")) ||
      normalizePlannerTarget(params?.plannerTarget ?? DEFAULT_PLANNING_THRESHOLD)
  );
  const utilitySharpness = Math.max(
    1,
    Number(searchParams.get("planner_sharpness")) ||
      DEFAULT_PLANNING_UTILITY_SHARPNESS
  );
  const reviewRatePerHour = Math.max(
    0,
    Number(searchParams.get("planner_review_rate")) || 0
  );
  const maxDelayMinutes = Math.max(
    horizonMinutes,
    Number(searchParams.get("planner_max_delay_minutes")) ||
      PLANNING_DELAY_MINUTES[PLANNING_DELAY_MINUTES.length - 1]
  );
  const beamDepth = Math.max(
    1,
    Math.round(
      Number(searchParams.get("planner_beam_depth")) ||
        DEFAULT_PLANNER_BEAM_DEPTH
    )
  );
  const beamWidth = Math.max(
    1,
    Math.round(
      Number(searchParams.get("planner_beam_width")) ||
        DEFAULT_PLANNER_BEAM_WIDTH
    )
  );
  const beamRootWidth = Math.max(
    beamWidth,
    Math.round(
      Number(searchParams.get("planner_beam_root_width")) ||
        DEFAULT_PLANNER_BEAM_ROOT_WIDTH
    )
  );
  const beamEvalCardCount = Math.max(
    beamRootWidth,
    Math.round(
      Number(searchParams.get("planner_beam_eval_cards")) ||
        DEFAULT_PLANNER_EVAL_CARD_COUNT
    )
  );
  const beamRandomFraction =
    beamRandomFractionParam == null
      ? DEFAULT_PLANNER_BEAM_RANDOM_FRACTION
      : clampProbability(Number(beamRandomFractionParam));
  const minUnder95Count = Math.max(
    0,
    Math.round(
      Number(searchParams.get("planner_min_under_95_count")) ||
        DEFAULT_MIN_UNDER_95_COUNT
    )
  );

  const candidateDelayMinutes = PLANNING_DELAY_MINUTES.filter(
    (minutes) => minutes <= maxDelayMinutes
  );
  if (
    candidateDelayMinutes[candidateDelayMinutes.length - 1] !== maxDelayMinutes
  ) {
    candidateDelayMinutes.push(maxDelayMinutes);
  }

  return {
    horizonMinutes,
    horizonHours: horizonMinutes / 60,
    threshold,
    utilitySharpness,
    reviewRatePerHour,
    beamDepth,
    beamWidth,
    beamRootWidth,
    beamEvalCardCount,
    beamRandomFraction,
    minUnder95Count,
    dueToleranceMinutes: DUE_NOW_TOLERANCE_MINUTES,
    maxDelayMinutes,
    candidateDelayMinutes,
  };
};

export const getRecentCardIdsFromHistory = (history, limit = 5) =>
  Object.values(history ?? {})
    .flat()
    .sort((left, right) => {
      const leftReview = Number(left?.review ?? Number.MAX_SAFE_INTEGER);
      const rightReview = Number(right?.review ?? Number.MAX_SAFE_INTEGER);
      if (leftReview !== rightReview) {
        return leftReview - rightReview;
      }

      return Number(left?.submitted_time ?? 0) - Number(right?.submitted_time ?? 0);
    })
    .slice(-limit)
    .map((review) => Number(review.card_id))
    .filter((cardId, index, items) =>
      Number.isFinite(cardId) && items.indexOf(cardId) === index
    );

export const chooseCardIdWithRandomization = ({
  rankedCards,
  fallbackCardId,
  avoidCard,
  state,
  recentCardIds = [],
  settings,
  random = Math.random,
}) => {
  const defaultCardId =
    rankedCards.find((cardId) => String(cardId) !== String(avoidCard)) ??
    fallbackCardId;

  const randomCardId = (candidates) => {
    if (candidates.length === 0) {
      return null;
    }

    const randomIndex = Math.min(
      Math.floor(random() * candidates.length),
      candidates.length - 1
    );
    return candidates[randomIndex];
  };

  const newCandidates = state.cards
    .filter(
      (card) => !card.seen && String(card.cardId) !== String(avoidCard)
    )
    .map((card) => card.cardId);
  if (
    settings.randomizeNew > 0 &&
    newCandidates.length > 0 &&
    random() < settings.randomizeNew
  ) {
    return randomCardId(newCandidates) ?? defaultCardId;
  }

  const recentCandidates = recentCardIds.filter(
    (cardId) =>
      String(cardId) !== String(defaultCardId) &&
      String(cardId) !== String(avoidCard)
  );
  if (
    settings.randomizeRecent > 0 &&
    recentCandidates.length > 0 &&
    random() < settings.randomizeRecent
  ) {
    return randomCardId(recentCandidates) ?? defaultCardId;
  }

  const seenCandidates = state.cards
    .filter(
      (card) =>
        card.seen &&
        String(card.cardId) !== String(defaultCardId) &&
        String(card.cardId) !== String(avoidCard)
    )
    .map((card) => card.cardId);
  if (
    settings.randomize > 0 &&
    seenCandidates.length > 0 &&
    random() < settings.randomize
  ) {
    return randomCardId(seenCandidates) ?? defaultCardId;
  }

  return defaultCardId;
};

export const getCurveProbability = (curveEntries, label) =>
  curveEntries.find((entry) => entry.label === label)?.probability ?? 0;

export const smoothThresholdUtility = (
  probability,
  threshold = DEFAULT_PLANNING_THRESHOLD,
  sharpness = DEFAULT_PLANNING_UTILITY_SHARPNESS
) => {
  const centered = sharpness * (clampProbability(probability) - threshold);
  return 1 / (1 + Math.exp(-centered));
};

const getReviewId = (review) =>
  Number(review?.review_id ?? review?.review ?? 0);

const getSubmittedTime = (review) => Number(review?.submitted_time ?? 0);

const getSourceId = (review) => Number(review?.source_id ?? 0);

const getCorrectAfter = (review) =>
  Number(review?.correct ?? 0) + (review?.was_correct ? 1 : 0);

const getIncorrectAfter = (review) =>
  Number(review?.incorrect ?? 0) + (review?.was_correct ? 0 : 1);

const getTotalReviewsAfter = (review) =>
  getCorrectAfter(review) + getIncorrectAfter(review);

const sortHistoryRows = (rows) =>
  [...rows].sort((left, right) => {
    const reviewGap = getReviewId(left) - getReviewId(right);
    if (reviewGap !== 0) {
      return reviewGap;
    }
    return getSubmittedTime(left) - getSubmittedTime(right);
  });

const compareReviewOrder = (left, right) => {
  const reviewGap = getReviewId(left) - getReviewId(right);
  if (reviewGap !== 0) {
    return reviewGap;
  }
  return getSubmittedTime(left) - getSubmittedTime(right);
};

const isSelectableCard = (card) => !card?.muted;

const canIncrementallyAppendHistory = (
  previousRows,
  nextRows,
  lastGlobalReview = null
) => {
  if (!Array.isArray(nextRows) || nextRows.length !== previousRows.length + 1) {
    return false;
  }

  for (let index = 0; index < previousRows.length; index += 1) {
    const previous = previousRows[index];
    const next = nextRows[index];
    if (
      previous !== next &&
      (getReviewId(previous) !== getReviewId(next) ||
        getSubmittedTime(previous) !== getSubmittedTime(next))
    ) {
      return false;
    }
  }

  const appendedReview = nextRows[nextRows.length - 1];
  const previousLastReview = previousRows[previousRows.length - 1];
  if (previousLastReview && compareReviewOrder(previousLastReview, appendedReview) > 0) {
    return false;
  }
  if (lastGlobalReview && compareReviewOrder(lastGlobalReview, appendedReview) > 0) {
    return false;
  }
  return true;
};

export const estimateReviewRatePerHour = (
  historyOrReviews,
  defaultRate = DEFAULT_REVIEW_RATE_PER_HOUR,
  windowSize = REVIEW_RATE_WINDOW
) => {
  const sortedReviews = Array.isArray(historyOrReviews)
    ? historyOrReviews
    : Object.values(historyOrReviews ?? {})
        .flat()
        .sort((left, right) => {
          const reviewGap = getReviewId(left) - getReviewId(right);
          if (reviewGap !== 0) {
            return reviewGap;
          }
          return getSubmittedTime(left) - getSubmittedTime(right);
        });
  const recentReviews = sortedReviews.slice(-windowSize);

  if (recentReviews.length < 2) {
    return {
      reviewRatePerHour: defaultRate,
      reviewsConsidered: recentReviews.length,
      medianGapSeconds: null,
      startTime: recentReviews[0] ? getSubmittedTime(recentReviews[0]) : null,
      endTime: recentReviews[recentReviews.length - 1]
        ? getSubmittedTime(recentReviews[recentReviews.length - 1])
        : null,
    };
  }

  const reviewGapsSeconds = recentReviews
    .slice(1)
    .map((review, index) =>
      Math.max(getSubmittedTime(review) - getSubmittedTime(recentReviews[index]), 0)
    )
    .filter((gapSeconds) => Number.isFinite(gapSeconds) && gapSeconds > 0)
    .sort((left, right) => left - right);

  if (reviewGapsSeconds.length === 0) {
    return {
      reviewRatePerHour: defaultRate,
      reviewsConsidered: recentReviews.length,
      medianGapSeconds: null,
      startTime: getSubmittedTime(recentReviews[0]),
      endTime: getSubmittedTime(recentReviews[recentReviews.length - 1]),
    };
  }

  const middleIndex = Math.floor(reviewGapsSeconds.length / 2);
  const medianGapSeconds =
    reviewGapsSeconds.length % 2 === 0
      ? (reviewGapsSeconds[middleIndex - 1] + reviewGapsSeconds[middleIndex]) / 2
      : reviewGapsSeconds[middleIndex];
  const reviewRatePerHour = Math.max(3600 / Math.max(medianGapSeconds, 1), 1);

  return {
    reviewRatePerHour,
    reviewsConsidered: recentReviews.length,
    medianGapSeconds,
    startTime: getSubmittedTime(recentReviews[0]),
    endTime: getSubmittedTime(recentReviews[recentReviews.length - 1]),
  };
};

export const estimateInterveningReviews = (delayHours, reviewRatePerHour) =>
  Math.max(0, Math.round(Math.max(delayHours, 0) * reviewRatePerHour));

export const estimateAverageReviewsPerDay = (
  reviews,
  now,
  windowDays = DELAYED_GAIN_RECENT_REVIEW_WINDOW_DAYS
) => {
  const effectiveWindowDays = Math.max(Number(windowDays) || 0, 1);
  const endTime = Number(now);
  if (!Number.isFinite(endTime)) {
    return 0;
  }

  const windowStart = endTime - effectiveWindowDays * 24 * 3600;
  const reviewsInWindow = (reviews ?? []).filter((review) => {
    const submittedTime = getSubmittedTime(review);
    return submittedTime >= windowStart && submittedTime <= endTime;
  }).length;

  return reviewsInWindow / effectiveWindowDays;
};

export const shouldUseDelayedReviewGainCandidates = (
  historyRows,
  now,
  {
    minimumTotalReviews = DELAYED_GAIN_MANY_REVIEW_MIN_TOTAL_REVIEWS,
    recentReviewHours = DELAYED_GAIN_RECENT_REVIEW_HOURS,
  } = {}
) => {
  if (!Array.isArray(historyRows) || historyRows.length === 0) {
    return false;
  }

  const lastReview = historyRows[historyRows.length - 1];
  const totalReviews = getTotalReviewsAfter(lastReview);
  if (totalReviews <= minimumTotalReviews) {
    return false;
  }

  const elapsedHours =
    Math.max(Number(now) - getSubmittedTime(lastReview), 0) / 3600;
  return elapsedHours <= recentReviewHours;
};

export const formatDelayLabel = (minutes) => {
  if (minutes === 0) {
    return "now";
  }

  if (minutes >= 1440 && minutes % 1440 === 0) {
    return `${minutes / 1440}d`;
  }

  if (minutes >= 60 && minutes % 60 === 0) {
    return `${minutes / 60}h`;
  }

  return `${minutes}m`;
};

const toRoundedNumber = (value, digits = 4) =>
  Number.isFinite(value) ? Number(value.toFixed(digits)) : null;

const average = (values) =>
  values.length > 0
    ? values.reduce((sum, value) => sum + value, 0) / values.length
    : null;

const formatDueIntervalParts = (seconds) => {
  const totalSeconds = Math.max(Number(seconds) || 0, 0);
  const totalMinutes = totalSeconds / 60;
  const totalHours = totalSeconds / 3600;
  const totalDays = totalSeconds / 86400;
  const wholeDays = Math.floor(totalSeconds / 86400);
  const wholeHours = Math.floor((totalSeconds % 86400) / 3600);
  const wholeMinutes = Math.floor((totalSeconds % 3600) / 60);
  return {
    totalDays: Number(totalDays.toFixed(4)),
    totalHours: Number(totalHours.toFixed(4)),
    totalMinutes: Number(totalMinutes.toFixed(4)),
    wholeDays,
    wholeHours,
    wholeMinutes,
  };
};

const getEuclideanDistance = (left = [], right = []) => {
  const length = Math.max(left.length, right.length);
  let sumSquared = 0;
  for (let index = 0; index < length; index += 1) {
    const delta = Number(left[index] ?? 0) - Number(right[index] ?? 0);
    sumSquared += delta * delta;
  }
  return Math.sqrt(sumSquared);
};

export const summarizePlansByBucket = (
  plans,
  bucketSelector,
  bucketField
) => {
  const grouped = plans.reduce((mapped, plan) => {
    const bucket = bucketSelector(plan);
    mapped[bucket] = mapped[bucket] ?? [];
    mapped[bucket].push(plan);
    return mapped;
  }, {});

  const bucketOrder = [
    ...TIMING_DIAGNOSTIC_BUCKETS.map((bucket) => bucket.label),
    NEVER_DUE_LABEL,
  ];
  const bucketRank = (bucket) => {
    const orderIndex = bucketOrder.indexOf(bucket);
    return orderIndex >= 0 ? orderIndex : bucketOrder.length;
  };

  return Object.entries(grouped)
    .sort(([leftBucket], [rightBucket]) => bucketRank(leftBucket) - bucketRank(rightBucket))
    .map(([bucket, bucketPlans]) => {
      const finiteDuePlans = bucketPlans.filter((plan) =>
        Number.isFinite(plan.dueInMinutes)
      );
      const scheduledPlans = bucketPlans.filter((plan) => plan.scheduled);
      return {
        [bucketField]: bucket,
        count: bucketPlans.length,
        dueNow: bucketPlans.filter((plan) => plan.dueNow).length,
        scheduled: scheduledPlans.length,
        unscheduled: bucketPlans.length - scheduledPlans.length,
        avgPNow: toRoundedNumber(average(bucketPlans.map((plan) => plan.pNow))),
        avgGain: toRoundedNumber(
          average(bucketPlans.map((plan) => plan.expectedGain))
        ),
        avgBestDelay: toRoundedNumber(
          average(bucketPlans.map((plan) => plan.bestDelayMinutes)),
          1
        ),
        avgHoursSinceSeen: toRoundedNumber(
          average(bucketPlans.map((plan) => plan.hoursSinceLastReview)),
          2
        ),
        avgDueInMinutes: toRoundedNumber(
          average(finiteDuePlans.map((plan) => plan.dueInMinutes)),
          1
        ),
        sampleCards: bucketPlans
          .slice(0, 3)
          .map((plan) => plan.front)
          .filter(Boolean)
          .join(" | "),
      };
    });
};

export const buildPlanningDelayQueries = (
  delayMinutes,
  reviewRatePerHour
) =>
  delayMinutes.map((minutes) => ({
    label: formatDelayLabel(minutes),
    minutes,
    hours: minutes / 60,
    reviews: estimateInterveningReviews(minutes / 60, reviewRatePerHour),
  }));

export const countCardsAboveRecallThreshold = (
  cards,
  curvesByCardId,
  label,
  threshold = TEN_MINUTE_RECALL_TARGET
) =>
  cards.reduce(
    (count, card) =>
      count +
      (getCurveProbability(curvesByCardId[Number(card.card_id)] ?? [], label) >
      threshold
        ? 1
        : 0),
    0
  );

export const buildSyntheticIncorrectReview = (cardId, reviewStep, now) => ({
  card_id: Number(cardId),
  review_id: reviewStep,
  review: reviewStep,
  submitted_time: now,
  showed_time: now,
  visible_time: 0,
  delta_t: 0,
  delta_r: 0,
  correct: 0,
  incorrect: 0,
  was_correct: false,
  side_shown: false,
  both_sides_mode: false,
});

export const buildSyntheticReview = ({
  cardId,
  historyRows,
  queryTime,
  queryReviewIndex,
  wasCorrect,
  sideShown = false,
  bothSidesMode = true,
}) => {
  const lastReview = historyRows[historyRows.length - 1];

  return {
    card_id: Number(cardId),
    review_id: queryReviewIndex,
    review: queryReviewIndex,
    submitted_time: queryTime,
    showed_time: queryTime,
    visible_time: 0,
    delta_t: lastReview ? Math.max(queryTime - getSubmittedTime(lastReview), 0) : 0,
    delta_r: lastReview ? Math.max(queryReviewIndex - getReviewId(lastReview), 0) : 0,
    correct: lastReview ? getCorrectAfter(lastReview) : 0,
    incorrect: lastReview ? getIncorrectAfter(lastReview) : 0,
    was_correct: !!wasCorrect,
    side_shown: !!sideShown,
    both_sides_mode: !!bothSidesMode,
  };
};

const normalizeHistoryMap = (history) =>
  Object.entries(history ?? {}).reduce((mapped, [cardId, historyRows]) => {
    mapped[cardId] = sortHistoryRows(historyRows ?? []);
    return mapped;
  }, {});

const buildHistoryCache = (history) => {
  const normalizedHistory = normalizeHistoryMap(history);
  const cardHistoriesById = new Map(
    Object.entries(normalizedHistory).map(([cardId, historyRows]) => [
      Number(cardId),
      historyRows,
    ])
  );
  const allReviewsChronological = Object.values(normalizedHistory)
    .flat()
    .sort((left, right) => {
      const reviewGap = getReviewId(left) - getReviewId(right);
      if (reviewGap !== 0) {
        return reviewGap;
      }
      return getSubmittedTime(left) - getSubmittedTime(right);
    });

  return {
    history: normalizedHistory,
    cardHistoriesById,
    allReviewsChronological,
    easyThresholdSeconds: computeEasyThresholdSecondsFromReviews(
      allReviewsChronological
    ),
    deckSummaryIndex: buildDeckSummaryIndex(normalizedHistory),
  };
};

export const chooseBestPlanCandidate = (candidates, gainTolerance = 1e-6) =>
  candidates.reduce((best, candidate) => {
    if (!best) {
      return candidate;
    }

    if (candidate.expectedGain > best.expectedGain + gainTolerance) {
      return candidate;
    }

    if (
      Math.abs(candidate.expectedGain - best.expectedGain) <= gainTolerance &&
      candidate.minutes < best.minutes
    ) {
      return candidate;
    }

    return best;
  }, null);

export const chooseSchedulingCandidate = (
  candidates,
  pNow,
  gainTolerance = 1e-6
) => {
  const bestCandidate = chooseBestPlanCandidate(candidates, gainTolerance);
  if (!bestCandidate) {
    return null;
  }

  if (bestCandidate.expectedHorizonProbability >= pNow) {
    return bestCandidate;
  }

  if (
    bestCandidate.baselineHorizonProbability < pNow &&
    bestCandidate.baselineHorizonProbability > 0.95
  ) {
    return bestCandidate;
  }

  return (
    candidates.find((candidate) => Math.abs(candidate.minutes) <= gainTolerance) ??
    bestCandidate
  );
};

const actionSideValue = (sideShown) => (sideShown ? 1 : 0);

export const getPlanActionKey = (cardId, sideShown = false) =>
  `${Number(cardId)}:${actionSideValue(sideShown)}`;

export const formatPlanSide = (sideShown) => (sideShown ? "back" : "front");

const getDelayedSiblingDueAt = (
  historyRows,
  sideShown = false,
  bothSidesMode = true
) => {
  if (!bothSidesMode || !Array.isArray(historyRows) || historyRows.length === 0) {
    return null;
  }

  const targetSideValue = actionSideValue(sideShown);
  const hasSeenTargetSide = historyRows.some(
    (review) => actionSideValue(review?.side_shown) === targetSideValue
  );
  if (hasSeenTargetSide) {
    return null;
  }

  const firstReview = historyRows[0] ?? null;
  const firstSubmittedTime = Number(firstReview?.submitted_time ?? 0);
  if (!Number.isFinite(firstSubmittedTime) || firstSubmittedTime <= 0) {
    return null;
  }

  return firstSubmittedTime + FSRS_NEW_SIBLING_DELAY_SECONDS;
};

const comparePlanIdentity = (left, right) => {
  const cardGap = Number(left?.cardId ?? 0) - Number(right?.cardId ?? 0);
  if (Math.abs(cardGap) > 1e-6) {
    return cardGap;
  }
  return actionSideValue(left?.sideShown) - actionSideValue(right?.sideShown);
};

export const sortDuePlans = (plans) =>
  [...plans].sort((left, right) => {
    const leftBeamValue = Number.isFinite(left.beamValue)
      ? left.beamValue
      : null;
    const rightBeamValue = Number.isFinite(right.beamValue)
      ? right.beamValue
      : null;
    if (leftBeamValue != null || rightBeamValue != null) {
      const beamGap = (rightBeamValue ?? -Infinity) - (leftBeamValue ?? -Infinity);
      if (Math.abs(beamGap) > 1e-6) {
        return beamGap;
      }
    }

    const gainGap = right.expectedGain - left.expectedGain;
    if (Math.abs(gainGap) > 1e-6) {
      return gainGap;
    }

    const probabilityGap =
      left.baselineHorizonProbability - right.baselineHorizonProbability;
    if (Math.abs(probabilityGap) > 1e-6) {
      return probabilityGap;
    }

    return comparePlanIdentity(left, right);
  });

export const sortUpcomingPlans = (plans) =>
  [...plans].sort((left, right) => {
    const leftBeamValue = Number.isFinite(left.beamValue)
      ? left.beamValue
      : null;
    const rightBeamValue = Number.isFinite(right.beamValue)
      ? right.beamValue
      : null;
    if (leftBeamValue != null || rightBeamValue != null) {
      const beamGap = (rightBeamValue ?? -Infinity) - (leftBeamValue ?? -Infinity);
      if (Math.abs(beamGap) > 1e-6) {
        return beamGap;
      }
    }

    const dueGap = left.dueAt - right.dueAt;
    if (Math.abs(dueGap) > 1e-6) {
      return dueGap;
    }

    const gainGap = (right.expectedGain ?? -Infinity) - (left.expectedGain ?? -Infinity);
    if (Math.abs(gainGap) > 1e-6) {
      return gainGap;
    }

    return comparePlanIdentity(left, right);
  });

export const chooseRandomCardId = (cardIds, avoidCard, random = Math.random) => {
  const candidates = cardIds.filter(
    (cardId) => String(cardId) !== String(avoidCard)
  );
  const effectiveCandidates = candidates.length > 0 ? candidates : [...cardIds];
  if (effectiveCandidates.length === 0) {
    return null;
  }

  const randomIndex = Math.min(
    Math.floor(random() * effectiveCandidates.length),
    effectiveCandidates.length - 1
  );
  return effectiveCandidates[randomIndex];
};

const chooseRandomPlan = (plans, avoidCard, random = Math.random) => {
  const candidates = plans.filter(
    (plan) => String(plan.cardId) !== String(avoidCard)
  );
  const effectiveCandidates = candidates.length > 0 ? candidates : [...plans];
  if (effectiveCandidates.length === 0) {
    return null;
  }

  const randomIndex = Math.min(
    Math.floor(random() * effectiveCandidates.length),
    effectiveCandidates.length - 1
  );
  return effectiveCandidates[randomIndex];
};

export const getCardSidelineStatusFromRecentEvents = (
  recentReviewEvents = [],
  cardId,
  {
    minimumCorrectReviews = SESSION_SIDELINE_MIN_CORRECT_REVIEWS,
    probabilityTolerance = 1e-6,
  } = {}
) => {
  const normalizedCardId = Number(cardId);
  if (!Number.isFinite(normalizedCardId)) {
    return false;
  }

  const matchingEvents = (recentReviewEvents ?? []).filter(
    (event) => Number(event?.cardId) === normalizedCardId
  );
  if (matchingEvents.some((event) => event?.wasCorrect !== true)) {
    return false;
  }

  const correctEvents = matchingEvents.filter((event) =>
    Number.isFinite(Number(event?.pCorrect))
  );

  if (correctEvents.length < minimumCorrectReviews) {
    return false;
  }

  for (let index = 1; index < correctEvents.length; index += 1) {
    const previousProbability = Number(correctEvents[index - 1].pCorrect);
    const currentProbability = Number(correctEvents[index].pCorrect);
    if (!(currentProbability < previousProbability - probabilityTolerance)) {
      return false;
    }
  }

  return true;
};

export const filterSessionSidelinedPlans = (
  plans = [],
  sidelinedCardIds = new Set()
) =>
  (plans ?? []).filter(
    (plan) => !sidelinedCardIds.has(Number(plan?.cardId))
  );

export const chooseNewCardSideShown = (
  bothSidesMode,
  sideShown = false,
  random = Math.random
) => (bothSidesMode ? random() < 0.5 : !!sideShown);

export const drawExplorationDelaySeconds = (
  random = Math.random,
  minimumSeconds = EXPLORATION_DELAY_MIN_SECONDS,
  maximumSeconds = EXPLORATION_DELAY_MAX_SECONDS
) =>
  minimumSeconds +
  clampProbability(random()) * Math.max(maximumSeconds - minimumSeconds, 0);

export const syncExplorationDelayByCardId = ({
  seenPlans,
  previousDelayByCardId = {},
  now,
  random = Math.random,
  fraction = EXPLORATION_SUPPRESSION_FRACTION,
}) => {
  const activeDelayByCardId = Object.entries(previousDelayByCardId ?? {}).reduce(
    (mapped, [cardId, availableAt]) => {
      const availableAtNumber = Number(availableAt);
      if (Number.isFinite(availableAtNumber) && availableAtNumber > now) {
        mapped[cardId] = availableAtNumber;
      }
      return mapped;
    },
    {}
  );
  const eligibleCardIds = uniqueNumbers(seenPlans.map((plan) => plan.cardId));
  const targetCount = Math.floor(eligibleCardIds.length * fraction);
  const activeEligibleCardIds = eligibleCardIds.filter(
    (cardId) => Number(activeDelayByCardId[cardId]) > now
  );

  const nextDelayByCardId = { ...activeDelayByCardId };
  if (activeEligibleCardIds.length > targetCount) {
    activeEligibleCardIds
      .sort(
        (left, right) =>
          Number(nextDelayByCardId[left]) - Number(nextDelayByCardId[right])
      )
      .slice(targetCount)
      .forEach((cardId) => {
        delete nextDelayByCardId[cardId];
      });
  }

  const remainingCount = Math.max(
    0,
    targetCount -
      eligibleCardIds.filter((cardId) => Number(nextDelayByCardId[cardId]) > now)
        .length
  );
  if (remainingCount <= 0) {
    return nextDelayByCardId;
  }

  const candidateCardIds = eligibleCardIds.filter(
    (cardId) => !(Number(nextDelayByCardId[cardId]) > now)
  );
  const sampledCardIds = [];
  while (
    candidateCardIds.length > 0 &&
    sampledCardIds.length < remainingCount
  ) {
    const randomIndex = Math.min(
      Math.floor(random() * candidateCardIds.length),
      candidateCardIds.length - 1
    );
    sampledCardIds.push(candidateCardIds.splice(randomIndex, 1)[0]);
  }

  sampledCardIds.forEach((cardId) => {
    nextDelayByCardId[cardId] =
      now + drawExplorationDelaySeconds(random);
  });

  return nextDelayByCardId;
};

export const applyExplorationSuppressionToPlans = (
  seenPlans,
  delayByCardId,
  now
) =>
  seenPlans.map((plan) => {
    const availableAt = Number(delayByCardId?.[plan.cardId] ?? 0);
    if (!Number.isFinite(availableAt) || availableAt <= now) {
      return {
        ...plan,
        explorationSuppressed: false,
        explorationAvailableAt: null,
      };
    }

    return {
      ...plan,
      pNow: 1,
      beamPNow: 1,
      dueNow: false,
      scheduled: false,
      expectedGain: 0,
      beamValue: 0,
      dueAt: availableAt,
      dueInMinutes: Math.max((availableAt - now) / 60, 0),
      explorationSuppressed: true,
      explorationAvailableAt: availableAt,
    };
  });

export const shouldPickRandomSeenCard = (
  random = Math.random,
  probability = RANDOM_SEEN_CARD_PROBABILITY
) => random() < probability;

export const shouldPickLowestProbabilitySeenCard = (
  random = Math.random,
  probability = LOWEST_PROBABILITY_CARD_SELECTION_PROBABILITY
) => random() < probability;

export const chooseHighGainLowProbabilityPlan = (
  plans,
  avoidCard,
  maxProbability = 0.96
) => {
  const selectablePlans = plans.filter(
    (plan) =>
      String(plan.cardId) !== String(avoidCard) &&
      Number(plan.pNow) < maxProbability
  );
  const fallbackPlans = plans.filter((plan) => Number(plan.pNow) < maxProbability);
  return (
    [...(selectablePlans.length > 0 ? selectablePlans : fallbackPlans)].sort(
      (left, right) => {
        const gainGap = (right.expectedGain ?? -Infinity) - (left.expectedGain ?? -Infinity);
        if (Math.abs(gainGap) > 1e-6) {
          return gainGap;
        }
        const pNowGap = (left.pNow ?? Infinity) - (right.pNow ?? Infinity);
        if (Math.abs(pNowGap) > 1e-6) {
          return pNowGap;
        }
        return comparePlanIdentity(left, right);
      }
    )[0] ?? null
  );
};

export const chooseMinimumProbabilityPlan = (plans, avoidCard) => {
  const selectablePlans = plans.filter(
    (plan) => String(plan.cardId) !== String(avoidCard)
  );
  const effectivePlans = selectablePlans.length > 0 ? selectablePlans : plans;
  return (
    [...effectivePlans].sort((left, right) => {
      const pNowGap = (left.pNow ?? Infinity) - (right.pNow ?? Infinity);
      if (Math.abs(pNowGap) > 1e-6) {
        return pNowGap;
      }
      const gainGap = (right.expectedGain ?? -Infinity) - (left.expectedGain ?? -Infinity);
      if (Math.abs(gainGap) > 1e-6) {
        return gainGap;
      }
      return comparePlanIdentity(left, right);
    })[0] ?? null
  );
};

export const chooseLowestAbsoluteProbabilityPlan = chooseMinimumProbabilityPlan;

export const chooseMinimumProbabilityUnderThresholdPlan = (
  plans,
  avoidCard,
  threshold = TEN_MINUTE_RECALL_TARGET
) => {
  const selectablePlans = plans.filter(
    (plan) =>
      String(plan.cardId) !== String(avoidCard) &&
      Number(plan.pNow) < threshold
  );
  const effectivePlans = selectablePlans.length > 0 ? selectablePlans : [];
  return (
    [...effectivePlans].sort((left, right) => {
      const pNowGap = (left.pNow ?? Infinity) - (right.pNow ?? Infinity);
      if (Math.abs(pNowGap) > 1e-6) {
        return pNowGap;
      }
      const gainGap =
        (right.expectedGain ?? -Infinity) - (left.expectedGain ?? -Infinity);
      if (Math.abs(gainGap) > 1e-6) {
        return gainGap;
      }
      return comparePlanIdentity(left, right);
    })[0] ?? null
  );
};

export const chooseMostRecentWrongPlan = (
  plans,
  cardHistoriesById,
  avoidCard
) => {
  const effectivePlans = plans.filter((plan) => {
    if (String(plan.cardId) === String(avoidCard)) {
      return false;
    }
    const historyRows = cardHistoriesById.get(Number(plan.cardId)) ?? [];
    const matchingHistory = historyRows.filter(
      (review) => actionSideValue(review?.side_shown) === actionSideValue(plan.sideShown)
    );
    const lastReview = matchingHistory[matchingHistory.length - 1];
    return lastReview && !lastReview.was_correct;
  });
  return (
    [...effectivePlans].sort((left, right) => {
      const leftHistory = cardHistoriesById.get(Number(left.cardId)) ?? [];
      const rightHistory = cardHistoriesById.get(Number(right.cardId)) ?? [];
      const leftReview = [...leftHistory]
        .reverse()
        .find(
          (review) =>
            actionSideValue(review?.side_shown) === actionSideValue(left.sideShown)
        );
      const rightReview = [...rightHistory]
        .reverse()
        .find(
          (review) =>
            actionSideValue(review?.side_shown) === actionSideValue(right.sideShown)
        );
      const timeGap = getSubmittedTime(rightReview) - getSubmittedTime(leftReview);
      if (Math.abs(timeGap) > 1e-6) {
        return timeGap;
      }
      return comparePlanIdentity(left, right);
    })[0] ?? null
  );
};

export const chooseWeightedSeenPlan = ({
  plans,
  avoidCard,
  cardHistoriesById,
  explorationModeEnabled = false,
  random = Math.random,
}) => {
  const minimumProbabilityUnderThresholdPlan =
    chooseMinimumProbabilityUnderThresholdPlan(
      plans,
      avoidCard,
      TEN_MINUTE_RECALL_TARGET
    );
  const minimumProbabilityPlan = chooseMinimumProbabilityPlan(plans, avoidCard);
  const randomCardId = chooseRandomCardId(
    uniqueNumbers(plans.map((plan) => plan.cardId)),
    avoidCard,
    random
  );
  const randomPlanPool = plans.filter(
    (plan) => Number(plan.cardId) === Number(randomCardId)
  );
  const randomPlan =
    randomCardId == null
      ? null
      : randomPlanPool[
          Math.min(
            Math.floor(random() * randomPlanPool.length),
            Math.max(randomPlanPool.length - 1, 0)
          )
        ] ?? null;
  const roll = random();
  const auditProbability = explorationModeEnabled
    ? EXPLORATION_RANDOM_SEEN_SELECTION_PROBABILITY
    : AUDIT_RANDOM_SEEN_SELECTION_PROBABILITY;
  const isAuditSelection = roll < auditProbability;
  const selectedPlan = isAuditSelection
    ? randomPlan ?? minimumProbabilityUnderThresholdPlan ?? minimumProbabilityPlan
    : minimumProbabilityUnderThresholdPlan ?? minimumProbabilityPlan ?? randomPlan;
  const selectionMode = isAuditSelection
    ? randomPlan
      ? "audit_random_seen"
      : minimumProbabilityUnderThresholdPlan
        ? "minimum_probability_under_95"
        : "minimum_probability_fallback"
    : minimumProbabilityUnderThresholdPlan
      ? "minimum_probability_under_95"
      : minimumProbabilityPlan
        ? "minimum_probability_fallback"
        : "audit_random_seen";
  return {
    plan: selectedPlan,
    selectionMode,
  };
};

const getPlannerStepSeconds = (reviewRatePerHour) =>
  3600 / Math.max(reviewRatePerHour, 1);

const uniqueNumbers = (values) =>
  [...new Set(values.filter((value) => Number.isFinite(Number(value))).map(Number))];

export const getTopCachedBeamCandidateIds = ({
  seenPlans,
  lastBeamValueByCardId,
  selectedCardId,
  avoidCard,
  limit = 3,
}) =>
  [...seenPlans]
    .filter(
      (plan) =>
        String(plan.cardId) !== String(selectedCardId) &&
        String(plan.cardId) !== String(avoidCard) &&
        Number.isFinite(lastBeamValueByCardId?.[plan.cardId])
    )
    .sort((left, right) => {
      const beamGap =
        (lastBeamValueByCardId[right.cardId] ?? -Infinity) -
        (lastBeamValueByCardId[left.cardId] ?? -Infinity);
      if (Math.abs(beamGap) > 1e-6) {
        return beamGap;
      }

      return left.cardId - right.cardId;
    })
    .slice(0, limit)
    .map((plan) => plan.cardId);

export const chooseRandomSample = (
  values,
  count,
  random = Math.random
) => {
  const pool = [...values];
  const sample = [];

  while (pool.length > 0 && sample.length < count) {
    const randomIndex = Math.min(
      Math.floor(random() * pool.length),
      pool.length - 1
    );
    sample.push(pool.splice(randomIndex, 1)[0]);
  }

  return sample;
};

export const buildBeamCandidateCardIds = ({
  seenPlans,
  recentFailedPlans,
  lowestPNowPlans,
  previousBeamRunnerUpCardId = null,
  unseenRepresentativeCardId = null,
  rootWidth,
  randomFraction = DEFAULT_PLANNER_BEAM_RANDOM_FRACTION,
  random = Math.random,
}) => {
  const fixedIds = uniqueNumbers([
    unseenRepresentativeCardId,
    previousBeamRunnerUpCardId,
  ]).slice(0, rootWidth);
  const remainingCapacity = Math.max(rootWidth - fixedIds.length, 0);
  const heuristicIds = uniqueNumbers([
    ...recentFailedPlans.map((plan) => plan.cardId),
    ...lowestPNowPlans.map((plan) => plan.cardId),
  ]).filter((cardId) => !fixedIds.includes(cardId));
  const desiredRandomCount = Math.min(
    remainingCapacity,
    Math.round(rootWidth * randomFraction)
  );
  const heuristicCount = Math.max(remainingCapacity - desiredRandomCount, 0);
  const chosenHeuristicIds = heuristicIds.slice(0, heuristicCount);
  const excludedIds = new Set([...fixedIds, ...chosenHeuristicIds]);
  const randomPool = seenPlans
    .map((plan) => plan.cardId)
    .filter((cardId) => !excludedIds.has(cardId));
  const chosenRandomIds = chooseRandomSample(
    randomPool,
    Math.min(desiredRandomCount, randomPool.length),
    random
  );

  return uniqueNumbers([
    ...fixedIds,
    ...chosenHeuristicIds,
    ...chosenRandomIds,
  ]).slice(0, rootWidth);
};

export const filterActiveSetByTenMinuteRecall = (
  activeCardIds,
  curvesByCardId,
  target = TEN_MINUTE_RECALL_TARGET
) =>
  activeCardIds.filter(
    (cardId) => getCurveProbability(curvesByCardId[cardId] ?? [], "10m") < target
  );

export const buildReplacementRanking = ({
  cards,
  history,
  activeCardIds,
  curvesByCardId,
  unseenRepresentativeCardId = null,
  tenMinuteTarget = TEN_MINUTE_RECALL_TARGET,
}) => {
  const activeSet = new Set(activeCardIds.map(String));
  const unseenTemplate = unseenRepresentativeCardId == null
    ? null
    : curvesByCardId[unseenRepresentativeCardId] ?? [];

  const candidates = cards
    .filter((card) => !activeSet.has(String(card.card_id)))
    .map((card) => {
      const cardId = Number(card.card_id);
      const cardHistory = history[String(cardId)] ?? history[cardId] ?? [];
      const curve =
        cardHistory.length === 0 && unseenTemplate.length > 0
          ? unseenTemplate
          : curvesByCardId[cardId] ?? [];
      return {
        cardId,
        isUnseen: cardHistory.length === 0,
        pTenMinutes: getCurveProbability(curve, "10m"),
        pThreeDays: getCurveProbability(curve, "3d"),
      };
    });

  const eligible = candidates.filter(
    (candidate) => candidate.pTenMinutes < tenMinuteTarget
  );
  const ranked = (eligible.length > 0 ? eligible : candidates).sort(
    (left, right) => right.pTenMinutes - left.pTenMinutes
  );

  return ranked;
};

const buildPlanningActions = (cards, bothSidesMode, sideShown = false) =>
  cards.flatMap((card) => {
    const cardId = Number(card.card_id);
    if (!bothSidesMode) {
      return [{ cardId, sideShown: !!sideShown }];
    }
    return [
      { cardId, sideShown: false },
      { cardId, sideShown: true },
    ];
  });

const buildWeakCardCount = (plans, bothSidesMode, threshold = 0.95) => {
  if (!bothSidesMode) {
    return plans.filter((plan) => Number(plan.pNow) < threshold).length;
  }

  const weakestByCard = plans.reduce((mapped, plan) => {
    const cardId = Number(plan.cardId);
    const currentValue = mapped.get(cardId);
    const nextValue =
      currentValue == null ? Number(plan.pNow) : Math.min(currentValue, Number(plan.pNow));
    mapped.set(cardId, nextValue);
    return mapped;
  }, new Map());

  return [...weakestByCard.values()].filter((value) => value < threshold).length;
};

const buildProbabilityExamples = (requests, metadata) =>
  requests.map((request) => {
    const reviewCountSaturationMax = getReviewCountSaturationMax(metadata);
    const { sequenceFeatures, sequenceMask } = buildSequenceInputs(
      request.historyRows,
      request.queryTime,
      metadata.max_history,
      metadata.sequence_feature_names.length,
      getEffectiveInferenceHistoryWindow(metadata),
      reviewCountSaturationMax
    );

    return {
      ...request,
      sequenceFeatures,
      sequenceMask,
      stateFeatures: buildStateFeatures(
        request.historyRows,
        {
          sideShown: request.sideShown,
          bothSidesMode: request.bothSidesMode,
          queryTime: request.queryTime,
          queryReviewIndex: request.queryReviewIndex,
          deckSummary: request.deckSummary,
          deckHistoryRows: request.deckHistoryRows ?? request.historyRows,
          reviewCountSaturationMax,
        },
        metadata.state_feature_names
      ),
      delayFeatures: buildDelayFeatures(
        request.historyRows,
        request.queryTime,
        request.queryReviewIndex,
        reviewCountSaturationMax
      ),
    };
  });

export class LSTM {
  constructor(history, cards) {
    this.cards = cards;
    this.cardsById = new Map(
      (cards ?? []).map((card) => [Number(card.card_id), card])
    );
    this.history = {};
    this.cardHistoriesById = new Map();
    this.allReviewsChronological = [];
    this.fsrsEasyThresholdSeconds = null;
    this.deckSummaryIndex = buildDeckSummaryIndex({});
    this.forgettingCurveMetadata = null;
    this.forgettingCurveCalibration = undefined;
    this.forgettingCurveSession = null;
    this.lastSelection = null;
    this.lastScheduledFsrsState = null;
    this.lastReviewStep = 0;
    this.cardPlanningCache = {};
    this.lastBeamValueByCardId = {};
    this.lastPlannerState = null;
    this.explorationDelayByCardId = {};
    this.sessionRecentReviewEvents = [];
    this.sessionSidelinedCardIds = new Set();
    this.params = {
      useRandomOrder: true,
      model: "FSRS",
      plannerTarget: DEFAULT_PLANNER_TARGET,
      fsrsFuzz: DEFAULT_ENABLE_FUZZ,
      fsrsDelaySiblings: true,
      fsrsEnableShortTerm: DEFAULT_ENABLE_SHORT_TERM,
      fsrsLearningStepsMinutes: [...DEFAULT_LEARNING_STEPS_MINUTES],
      fsrsRelearningStepsMinutes: [...DEFAULT_RELEARNING_STEPS_MINUTES],
      fsrsParameters: [...DEFAULT_FSRS_PARAMETERS],
    };
    this.schedulerModel = "FSRS";
    this.fsrsParameters = [...DEFAULT_FSRS_PARAMETERS];
    this.plannerTarget = DEFAULT_PLANNER_TARGET;
    this.onFsrsParamsOptimized = null;
    this.fsrsOptimizationInFlight = false;
    this.fsrsOptimizationPromise = null;
    this.pendingFsrsOptimizationReviewCount = null;
    this.lastFsrsOptimizationReviewCount = 0;
    this.clearFsrsScheduleCache();
    this.setHistory(history);
  }

  setParams(params = {}, onFsrsParamsOptimized = undefined) {
    const previousCacheSignature = this.getFsrsScheduleCacheSignature();
    this.params = {
      ...this.params,
      ...params,
      useRandomOrder: normalizeBooleanFlag(
        params?.useRandomOrder ?? this.params.useRandomOrder,
        this.params.useRandomOrder
      ),
      model: normalizeSchedulerModel(params?.model ?? this.params.model),
      plannerTarget: normalizePlannerTarget(
        params?.plannerTarget ?? this.params.plannerTarget
      ),
      fsrsFuzz: normalizeBooleanFlag(
        params?.fsrsFuzz ?? this.params.fsrsFuzz,
        this.params.fsrsFuzz
      ),
      fsrsDelaySiblings: normalizeBooleanFlag(
        params?.fsrsDelaySiblings ?? this.params.fsrsDelaySiblings,
        this.params.fsrsDelaySiblings
      ),
      fsrsEnableShortTerm: normalizeBooleanFlag(
        params?.fsrsEnableShortTerm ?? this.params.fsrsEnableShortTerm,
        this.params.fsrsEnableShortTerm
      ),
      fsrsLearningStepsMinutes: normalizeStepMinutes(
        params?.fsrsLearningStepsMinutes ?? this.params.fsrsLearningStepsMinutes,
        this.params.fsrsLearningStepsMinutes
      ),
      fsrsRelearningStepsMinutes: normalizeStepMinutes(
        params?.fsrsRelearningStepsMinutes ??
          this.params.fsrsRelearningStepsMinutes,
        this.params.fsrsRelearningStepsMinutes
      ),
      fsrsParameters: normalizeFsrsParameters(
        params?.fsrsParameters ?? this.params.fsrsParameters
      ),
    };
    this.schedulerModel = this.params.model;
    this.plannerTarget = this.params.plannerTarget;
    this.fsrsParameters = [...this.params.fsrsParameters];
    if (previousCacheSignature !== this.getFsrsScheduleCacheSignature()) {
      this.clearFsrsScheduleCache();
    }
    if (onFsrsParamsOptimized !== undefined) {
      this.onFsrsParamsOptimized = onFsrsParamsOptimized;
    }
  }

  getFsrsSchedulingConfig() {
    return {
      fuzz: this.params.fsrsFuzz,
      delaySiblings: this.params.fsrsDelaySiblings,
      enableShortTerm: this.params.fsrsEnableShortTerm,
      learningStepsMinutes: this.params.fsrsLearningStepsMinutes,
      relearningStepsMinutes: this.params.fsrsRelearningStepsMinutes,
      easyThresholdSeconds: this.fsrsEasyThresholdSeconds,
    };
  }

  getFsrsScheduleCacheSignature() {
    return JSON.stringify({
      model: this.schedulerModel,
      target: this.plannerTarget,
      weights: this.fsrsParameters,
      config: this.getFsrsSchedulingConfig(),
    });
  }

  clearFsrsScheduleCache() {
    this.fsrsScheduleCache = {
      contextKey: null,
      context: null,
      plansByActionKey: new Map(),
      seenActionKeys: [],
      unseenActionKeys: [],
    };
  }

  getFsrsContextKey({ sideShown = false, bothSidesMode = true } = {}) {
    return JSON.stringify({
      scheduleSignature: this.getFsrsScheduleCacheSignature(),
      sideShown: !!sideShown,
      bothSidesMode: !!bothSidesMode,
    });
  }

  buildFsrsStaticPlanFromState(
    card,
    sideShown,
    state,
    {
      dueAtOverride = undefined,
      phaseOverride = undefined,
      schedulingReasonOverride = undefined,
      lastReviewTimeOverride = undefined,
    } = {}
  ) {
    const cardId = Number(card.card_id);
    const projection = projectFsrsStateAtTime({
      state,
      now: Number(state?.lastReviewTime ?? state?.dueAt ?? 0) || 0,
      weights: this.fsrsParameters,
      desiredRetention: this.plannerTarget,
      config: this.getFsrsSchedulingConfig(),
    });
    return {
      actionKey: getPlanActionKey(cardId, sideShown),
      cardId,
      sideShown: !!sideShown,
      sideLabel: formatPlanSide(sideShown),
      isUnseen: state == null,
      phase: phaseOverride ?? state?.phase ?? null,
      dueAt: dueAtOverride ?? state?.dueAt ?? null,
      baseDueAt: dueAtOverride ?? state?.dueAt ?? null,
      dueInMinutes: null,
      intervalDays: projection.intervalDays,
      minIntervalDays: projection.minIntervalDays,
      maxIntervalDays: projection.maxIntervalDays,
      lastReviewTime: lastReviewTimeOverride ?? state?.lastReviewTime ?? null,
      schedulingReason:
        schedulingReasonOverride ?? state?.schedulingReason ?? null,
      front: card.front ?? "",
      state,
    };
  }

  buildFsrsStaticPlan(card, sideShown, bothSidesMode) {
    const cardId = Number(card.card_id);
    const historyRows = this.cardHistoriesById.get(cardId) ?? [];
    const replayed = replayFsrsHistory({
      historyRows,
      sideShown,
      bothSidesMode,
      weights: this.fsrsParameters,
      desiredRetention: this.plannerTarget,
      config: this.getFsrsSchedulingConfig(),
    });
    const delayedSiblingDueAt = getDelayedSiblingDueAt(
      historyRows,
      sideShown,
      bothSidesMode
    );
    return this.buildFsrsStaticPlanFromState(card, sideShown, replayed.state, {
      dueAtOverride: delayedSiblingDueAt,
      phaseOverride: delayedSiblingDueAt == null ? undefined : "new_sibling_delayed",
      schedulingReasonOverride:
        delayedSiblingDueAt == null
          ? undefined
          : "new_sibling_delayed_one_hour",
      lastReviewTimeOverride:
        delayedSiblingDueAt == null
          ? undefined
          : Number(historyRows[0]?.submitted_time ?? null),
    });
  }

  applyFsrsSiblingDispersionToPlans(plans) {
    if (!this.params.fsrsDelaySiblings) {
      return plans.map((plan) => ({
        ...plan,
        dueAt: plan.baseDueAt,
      }));
    }

    const normalizedPlans = plans.map((plan) => ({
      ...plan,
      dueAt: plan.baseDueAt,
    }));
    if (normalizedPlans.length < 2) {
      return normalizedPlans;
    }
    if (
      normalizedPlans.some(
        (plan) =>
          plan.isUnseen ||
          plan.phase !== "review" ||
          !Number.isFinite(plan.lastReviewTime) ||
          !Number.isFinite(plan.minIntervalDays) ||
          !Number.isFinite(plan.maxIntervalDays)
      )
    ) {
      return normalizedPlans;
    }

    const sortedSiblings = [...normalizedPlans].sort((left, right) => {
      const dueGap = Number(left.baseDueAt ?? 0) - Number(right.baseDueAt ?? 0);
      if (Math.abs(dueGap) > 1e-6) {
        return dueGap;
      }
      return Number(left.sideShown) - Number(right.sideShown);
    });

    const assignedDueAtByActionKey = new Map();
    sortedSiblings.forEach((plan, siblingIndex) => {
      const assignedIntervalDays =
        siblingIndex <= 0 ? plan.minIntervalDays : plan.maxIntervalDays;
      assignedDueAtByActionKey.set(
        plan.actionKey,
        Number(plan.lastReviewTime) + Number(assignedIntervalDays) * 86400
      );
    });

    return normalizedPlans.map((plan) => ({
      ...plan,
      dueAt: assignedDueAtByActionKey.get(plan.actionKey) ?? plan.baseDueAt,
    }));
  }

  ensureFsrsScheduleCache({ sideShown = false, bothSidesMode = true } = {}) {
    const contextKey = this.getFsrsContextKey({ sideShown, bothSidesMode });
    if (this.fsrsScheduleCache.contextKey === contextKey) {
      return this.fsrsScheduleCache;
    }
    return this.rebuildFsrsScheduleCache({ sideShown, bothSidesMode });
  }

  rebuildFsrsScheduleCache({ sideShown = false, bothSidesMode = true } = {}) {
    const plansByActionKey = new Map();
    const seenActionKeys = [];
    const unseenActionKeys = [];
    const selectableCards = this.cards.filter(isSelectableCard);

    selectableCards.forEach((card) => {
      const sides = bothSidesMode ? [false, true] : [!!sideShown];
      const siblingPlans = this.applyFsrsSiblingDispersionToPlans(
        sides.map((nextSideShown) =>
          this.buildFsrsStaticPlan(card, nextSideShown, bothSidesMode)
        )
      );
      siblingPlans.forEach((plan) => {
        plansByActionKey.set(plan.actionKey, plan);
        if (plan.isUnseen) {
          unseenActionKeys.push(plan.actionKey);
        } else {
          seenActionKeys.push(plan.actionKey);
        }
      });
    });

    this.fsrsScheduleCache = {
      contextKey: this.getFsrsContextKey({ sideShown, bothSidesMode }),
      context: {
        sideShown: !!sideShown,
        bothSidesMode: !!bothSidesMode,
      },
      plansByActionKey,
      seenActionKeys,
      unseenActionKeys,
    };
    return this.fsrsScheduleCache;
  }

  buildFsrsLoggedStateFromPreviousSequence(
    cardId,
    appendedReview,
    previousSequence = []
  ) {
    if (!appendedReview) {
      return null;
    }
    const bothSidesMode = !!appendedReview?.both_sides_mode;
    const sideShown = bothSidesMode ? !!appendedReview?.side_shown : false;
    const previousState = replayFsrsHistory({
      historyRows: previousSequence,
      sideShown,
      bothSidesMode,
      weights: this.fsrsParameters,
      desiredRetention: this.plannerTarget,
      config: this.getFsrsSchedulingConfig(),
    }).state;
    return advanceFsrsState({
      state: previousState,
      review: appendedReview,
      weights: this.fsrsParameters,
      desiredRetention: this.plannerTarget,
      config: this.getFsrsSchedulingConfig(),
    });
  }

  logFsrsDueState(review, state) {
    if (!review || !state || !Number.isFinite(Number(state.dueAt))) {
      return;
    }
    this.lastScheduledFsrsState = {
      review,
      state,
    };
    const submittedTime = Number(review.submitted_time ?? 0);
    const intervalSeconds = Math.max(Number(state.dueAt) - submittedTime, 0);
    const parts = formatDueIntervalParts(intervalSeconds);
    const details = [
      "[fsrs] due_at",
      `card=${Number(review.card_id) || "unknown"}`,
      `phase=${state.phase ?? "unknown"}`,
      `reason=${state.schedulingReason ?? "unknown"}`,
      `days=${parts.totalDays}`,
      `hours=${parts.totalHours}`,
      `mins=${parts.totalMinutes}`,
      `breakdown=${parts.wholeDays}d${parts.wholeHours}h${parts.wholeMinutes}m`,
      `due_at=${Number(Number(state.dueAt).toFixed(3))}`,
    ];
    if (Number.isFinite(Number(state.targetIntervalDays))) {
      details.push(
        `interval_days=${Number(Number(state.targetIntervalDays).toFixed(4))}`
      );
    }
    if (Number.isFinite(Number(state.scheduledIntervalDays))) {
      details.push(
        `scheduled_interval_days=${Number(
          Number(state.scheduledIntervalDays).toFixed(4)
        )}`
      );
    }
    if (Number.isFinite(Number(state.scheduledStepMinutes))) {
      details.push(
        `step_mins=${Number(Number(state.scheduledStepMinutes).toFixed(4))}`
      );
    }
    if (this.params.fsrsFuzz) {
      details.push("fuzz=true");
    }
    console.log(details.join(" "));
  }

  updateFsrsScheduleCacheForCard(
    cardId,
    appendedReview = null,
    previousSequence = []
  ) {
    const context = this.fsrsScheduleCache?.context;
    if (!this.fsrsScheduleCache?.contextKey || !context) {
      if (appendedReview) {
        const reviewedState = this.buildFsrsLoggedStateFromPreviousSequence(
          cardId,
          appendedReview,
          previousSequence
        );
        this.logFsrsDueState(appendedReview, reviewedState);
      }
      return;
    }

    const { sideShown, bothSidesMode } = context;
    const card = this.cardsById.get(Number(cardId));
    if (!card || !isSelectableCard(card)) {
      const nextPlans = new Map(this.fsrsScheduleCache.plansByActionKey);
      const nextSeenKeys = this.fsrsScheduleCache.seenActionKeys.filter(
        (actionKey) => Number(actionKey.split(":")[0]) !== Number(cardId)
      );
      const nextUnseenKeys = this.fsrsScheduleCache.unseenActionKeys.filter(
        (actionKey) => Number(actionKey.split(":")[0]) !== Number(cardId)
      );
      [...nextPlans.keys()].forEach((actionKey) => {
        if (Number(actionKey.split(":")[0]) === Number(cardId)) {
          nextPlans.delete(actionKey);
        }
      });
      this.fsrsScheduleCache = {
        ...this.fsrsScheduleCache,
        plansByActionKey: nextPlans,
        seenActionKeys: nextSeenKeys,
        unseenActionKeys: nextUnseenKeys,
      };
      return;
    }

    if (appendedReview) {
      const config = this.getFsrsSchedulingConfig();
      const reviewedSideShown = bothSidesMode
        ? !!appendedReview?.side_shown
        : !!sideShown;
      const reviewedActionKey = getPlanActionKey(cardId, reviewedSideShown);
      const currentReviewedPlan =
        this.fsrsScheduleCache.plansByActionKey.get(reviewedActionKey) ?? null;
      if (currentReviewedPlan || !bothSidesMode) {
        const nextPlans = new Map(this.fsrsScheduleCache.plansByActionKey);
        const nextSeenKeys = this.fsrsScheduleCache.seenActionKeys.filter(
          (actionKey) => Number(actionKey.split(":")[0]) !== Number(cardId)
        );
        const nextUnseenKeys = this.fsrsScheduleCache.unseenActionKeys.filter(
          (actionKey) => Number(actionKey.split(":")[0]) !== Number(cardId)
        );
        const reviewedState =
          currentReviewedPlan != null
            ? advanceFsrsState({
                state: currentReviewedPlan.state ?? null,
                review: appendedReview,
                weights: this.fsrsParameters,
                desiredRetention: this.plannerTarget,
                config,
              })
            : this.buildFsrsLoggedStateFromPreviousSequence(
                cardId,
                appendedReview,
                previousSequence
              );
        this.logFsrsDueState(appendedReview, reviewedState);
        const siblingSides = bothSidesMode
          ? [false, true]
          : [!!sideShown];
        const siblingPlans = this.applyFsrsSiblingDispersionToPlans(
          siblingSides.map((nextSideShown) => {
            if (nextSideShown === reviewedSideShown) {
              return this.buildFsrsStaticPlanFromState(
                card,
                nextSideShown,
                reviewedState
              );
            }
            return this.buildFsrsStaticPlan(card, nextSideShown, bothSidesMode);
          })
        );
        siblingPlans.forEach((plan) => {
          nextPlans.set(plan.actionKey, plan);
          if (plan.isUnseen) {
            nextUnseenKeys.push(plan.actionKey);
          } else {
            nextSeenKeys.push(plan.actionKey);
          }
        });
        this.fsrsScheduleCache = {
          ...this.fsrsScheduleCache,
          plansByActionKey: nextPlans,
          seenActionKeys: nextSeenKeys,
          unseenActionKeys: nextUnseenKeys,
        };
        return;
      }
      const reviewedState = this.buildFsrsLoggedStateFromPreviousSequence(
        cardId,
        appendedReview,
        previousSequence
      );
      this.logFsrsDueState(appendedReview, reviewedState);
    }

    const sides = bothSidesMode ? [false, true] : [!!sideShown];
    const nextPlans = new Map(this.fsrsScheduleCache.plansByActionKey);
    const nextSeenKeys = this.fsrsScheduleCache.seenActionKeys.filter(
      (actionKey) => Number(actionKey.split(":")[0]) !== Number(cardId)
    );
    const nextUnseenKeys = this.fsrsScheduleCache.unseenActionKeys.filter(
      (actionKey) => Number(actionKey.split(":")[0]) !== Number(cardId)
    );
    const siblingPlans = this.applyFsrsSiblingDispersionToPlans(
      sides.map((nextSideShown) =>
        this.buildFsrsStaticPlan(card, nextSideShown, bothSidesMode)
      )
    );

    siblingPlans.forEach((plan) => {
      nextPlans.set(plan.actionKey, plan);
      if (plan.isUnseen) {
        nextUnseenKeys.push(plan.actionKey);
      } else {
        nextSeenKeys.push(plan.actionKey);
      }
    });

    this.fsrsScheduleCache = {
      ...this.fsrsScheduleCache,
      plansByActionKey: nextPlans,
      seenActionKeys: nextSeenKeys,
      unseenActionKeys: nextUnseenKeys,
    };
  }

  materializeFsrsPlan(plan, now) {
    const projection = projectFsrsStateAtTime({
      state: plan.state,
      now,
      weights: this.fsrsParameters,
      desiredRetention: this.plannerTarget,
      config: this.getFsrsSchedulingConfig(),
    });

    return {
      ...plan,
      phase: plan.state?.phase ?? plan.phase ?? null,
      stepIndex: plan.state?.stepIndex ?? null,
      schedulingReason:
        plan.state?.schedulingReason ?? plan.schedulingReason ?? null,
      pNow: clampProbability(projection.probability ?? 0),
      dueInMinutes:
        plan.dueAt == null ? null : (Number(plan.dueAt) - Number(now)) / 60,
      intervalDays: plan.intervalDays,
      minIntervalDays: plan.minIntervalDays,
      maxIntervalDays: plan.maxIntervalDays,
      lastReviewTime: plan.state?.lastReviewTime ?? plan.lastReviewTime ?? null,
    };
  }

  getModelVersion() {
    return this.schedulerModel === "ML" ? "rl_planner" : "fsrs";
  }

  resetSessionSchedulingState() {
    this.sessionRecentReviewEvents = [];
    this.sessionSidelinedCardIds = new Set();
  }

  setHistory(history) {
    const cache = buildHistoryCache(history);
    this.history = cache.history;
    this.cardHistoriesById = cache.cardHistoriesById;
    this.allReviewsChronological = cache.allReviewsChronological;
    this.fsrsEasyThresholdSeconds = cache.easyThresholdSeconds;
    this.deckSummaryIndex = cache.deckSummaryIndex;
    this.clearFsrsScheduleCache();
    this.resetSessionSchedulingState();
  }

  printLSTMState() {
    console.log(
      `${Object.keys(this.history).length} history ${this.cards.length} cards`
    );
    if (this.schedulerModel === "ML") {
      console.log(
        `forgetting curve ready=${!!this.forgettingCurveSession && !!this.forgettingCurveMetadata}`
      );
      return;
    }
    console.log(
      `fsrs mode target=${this.plannerTarget} params=${this.fsrsParameters.length}`
    );
  }

  async loadForgettingCurveArtifacts() {
    if (
      this.forgettingCurveSession &&
      this.forgettingCurveMetadata &&
      this.forgettingCurveCalibration !== undefined
    ) {
      return;
    }

    const [metadataResponse, calibrationResponse] = await Promise.all([
      fetch(FORGETTING_CURVE_METADATA_URL),
      fetch(FORGETTING_CURVE_CALIBRATION_URL).catch(() => null),
    ]);
    if (!metadataResponse.ok) {
      throw new Error(
        `Failed to load forgetting-curve metadata: ${metadataResponse.status}`
      );
    }

    this.forgettingCurveMetadata = await metadataResponse.json();
    if (calibrationResponse?.ok) {
      this.forgettingCurveCalibration = normalizeIsotonicCalibration(
        await calibrationResponse.json()
      );
    } else {
      this.forgettingCurveCalibration = null;
    }
    this.forgettingCurveSession = await loadForgettingCurveSession();
  }

  getEvaluationDelaySeconds() {
    return 0;
  }

  shouldDebugCurve() {
    return hasTruthyQueryValue(getSearchParams().get("debug_curve"));
  }

  async ready() {
    if (this.schedulerModel === "ML") {
      await this.loadForgettingCurveArtifacts();
    }
    this.printLSTMState();
  }

  async readyForgettingCurve() {
    if (this.schedulerModel === "ML") {
      await this.loadForgettingCurveArtifacts();
    }
    this.printLSTMState();
  }

  async setSingleHistoryElement(sequence, cardId) {
    const normalizedCardId = Number(cardId);
    const historyKey = String(cardId);
    const nextSequence = [...sequence];
    const previousSequence = this.cardHistoriesById.get(normalizedCardId) ?? [];
    const lastGlobalReview =
      this.allReviewsChronological[this.allReviewsChronological.length - 1] ?? null;

    if (
      canIncrementallyAppendHistory(
        previousSequence,
        nextSequence,
        lastGlobalReview
      )
    ) {
      const appendedReview = nextSequence[nextSequence.length - 1];
      this.history[historyKey] = nextSequence;
      this.cardHistoriesById.set(normalizedCardId, nextSequence);
      this.allReviewsChronological.push(appendedReview);
      const previousEasyThresholdSeconds = this.fsrsEasyThresholdSeconds;
      this.fsrsEasyThresholdSeconds = computeEasyThresholdSecondsFromReviews(
        this.allReviewsChronological
      );
      this.deckSummaryIndex.allRows.push(appendedReview);
      this.deckSummaryIndex.allSummary = extendDeckSummary(
        this.deckSummaryIndex.allSummary,
        appendedReview
      );

      const sourceId = getSourceId(appendedReview);
      const sourceRows = this.deckSummaryIndex.rowsBySource.get(sourceId);
      if (sourceRows) {
        sourceRows.push(appendedReview);
      } else {
        this.deckSummaryIndex.rowsBySource.set(sourceId, [appendedReview]);
      }
      this.deckSummaryIndex.summariesBySource.set(
        sourceId,
        extendDeckSummary(
          this.deckSummaryIndex.summariesBySource.get(sourceId),
          appendedReview
        )
      );
      if (previousEasyThresholdSeconds !== this.fsrsEasyThresholdSeconds) {
        this.clearFsrsScheduleCache();
      } else {
        this.updateFsrsScheduleCacheForCard(
          normalizedCardId,
          appendedReview,
          previousSequence
        );
      }
      this.scheduleFsrsOptimizationIfNeeded();
      return;
    }

    this.setHistory({
      ...this.history,
      [historyKey]: nextSequence,
    });
    this.scheduleFsrsOptimizationIfNeeded();
  }

  recordSessionReviewEvent({ cardId, pCorrect, wasCorrect }) {
    const nextEvent = {
      cardId: Number(cardId),
      pCorrect: Number(pCorrect),
      wasCorrect: wasCorrect === true,
    };
    const nextEvents = [
      ...this.sessionRecentReviewEvents,
      nextEvent,
    ].slice(-SESSION_REVIEW_CACHE_LIMIT);
    this.sessionRecentReviewEvents = nextEvents;

    if (!nextEvent.wasCorrect) {
      this.sessionSidelinedCardIds.delete(nextEvent.cardId);
    } else if (
      getCardSidelineStatusFromRecentEvents(nextEvents, nextEvent.cardId)
    ) {
      this.sessionSidelinedCardIds.add(nextEvent.cardId);
    }

    return {
      sidelined: this.sessionSidelinedCardIds.has(nextEvent.cardId),
      recentReviewEvents: [...this.sessionRecentReviewEvents],
    };
  }

  scheduleFsrsOptimizationIfNeeded({ force = false } = {}) {
    if (this.schedulerModel !== "FSRS") {
      return Promise.resolve(null);
    }

    const totalReviews = this.allReviewsChronological.length;
    if (!force && (totalReviews < 100 || totalReviews % 100 !== 0)) {
      return Promise.resolve(null);
    }
    if (force && totalReviews < 100) {
      return Promise.resolve(null);
    }
    if (totalReviews <= this.lastFsrsOptimizationReviewCount) {
      return Promise.resolve(null);
    }
    if (this.fsrsOptimizationInFlight) {
      this.pendingFsrsOptimizationReviewCount = totalReviews;
      return this.fsrsOptimizationPromise ?? Promise.resolve(null);
    }

    this.fsrsOptimizationInFlight = true;
    this.pendingFsrsOptimizationReviewCount = null;
    this.fsrsOptimizationPromise = optimizeFsrsParameters({
      reviews: this.allReviewsChronological,
      initialWeights: this.fsrsParameters,
      desiredRetention: this.plannerTarget,
      config: {
        fuzz: this.params.fsrsFuzz,
        delaySiblings: this.params.fsrsDelaySiblings,
        enableShortTerm: this.params.fsrsEnableShortTerm,
        learningStepsMinutes: this.params.fsrsLearningStepsMinutes,
        relearningStepsMinutes: this.params.fsrsRelearningStepsMinutes,
      },
    })
      .then((optimizedWeights) => {
        const previousWeights = [...this.fsrsParameters];
        const updateSize = Number(
          getEuclideanDistance(optimizedWeights, previousWeights).toFixed(6)
        );
        this.fsrsParameters = [...optimizedWeights];
        this.params = {
          ...this.params,
          fsrsParameters: [...optimizedWeights],
          fsrsUpdatedAt: new Date().toISOString(),
          fsrsUpdateSize: updateSize,
        };
        this.clearFsrsScheduleCache();
        this.lastFsrsOptimizationReviewCount = totalReviews;
        if (typeof this.onFsrsParamsOptimized === "function") {
          void this.onFsrsParamsOptimized({
            ...this.params,
            model: this.schedulerModel,
            plannerTarget: this.plannerTarget,
            fsrsParameters: [...optimizedWeights],
          });
        }
      })
      .catch((error) => {
        console.error("FSRS parameter optimization failed", error);
        return null;
      })
      .finally(() => {
        this.fsrsOptimizationInFlight = false;
        this.fsrsOptimizationPromise = null;
        const pendingReviewCount = this.pendingFsrsOptimizationReviewCount;
        this.pendingFsrsOptimizationReviewCount = null;
        if (
          pendingReviewCount &&
          pendingReviewCount > this.lastFsrsOptimizationReviewCount
        ) {
          this.scheduleFsrsOptimizationIfNeeded();
        }
      });
    return this.fsrsOptimizationPromise;
  }

  trainFsrsImmediately() {
    return this.scheduleFsrsOptimizationIfNeeded({ force: true });
  }

  async predictProbabilitiesForRequests(requests) {
    if (requests.length === 0) {
      return {};
    }

    const examples = buildProbabilityExamples(
      requests,
      this.forgettingCurveMetadata
    );
    const probabilities = await predictForgettingCurveBatch(
      this.forgettingCurveSession,
      this.forgettingCurveMetadata,
      examples,
      this.forgettingCurveCalibration
    );

    return requests.reduce((mapped, request, index) => {
      mapped[request.key] = probabilities[index];
      return mapped;
    }, {});
  }

  async predictProbabilitiesForSearchState(
    searchState,
    actions,
    { sideShown = false, bothSidesMode = true } = {}
  ) {
    const requests = actions.map((action) => ({
      key: getPlanActionKey(action.cardId, action.sideShown),
      historyRows: searchState.cardHistoriesById.get(action.cardId) ?? [],
      deckSummary: searchState.deckSummary,
      queryTime: searchState.now,
      queryReviewIndex: searchState.reviewStep,
      sideShown: action.sideShown ?? sideShown,
      bothSidesMode,
    }));

    return this.predictProbabilitiesForRequests(requests);
  }

  rememberBeamValues(plans) {
    (plans ?? []).forEach((plan) => {
      if (Number.isFinite(plan?.beamValue) && Number.isFinite(plan?.cardId)) {
        this.lastBeamValueByCardId[plan.cardId] = plan.beamValue;
      }
    });
  }

  async rerankFocusedBeamCandidates(
    plannerState,
    candidateCardIds,
    { sideShown = false, bothSidesMode = true } = {}
  ) {
    const focusedCardIds = uniqueNumbers(candidateCardIds);
    if (focusedCardIds.length <= 1) {
      return [];
    }

    const refinedBeamScores = await this.scoreBeamSearchCandidates(
      plannerState.searchState,
      focusedCardIds,
      focusedCardIds.slice(0, plannerState.settings.beamWidth),
      uniqueNumbers([
        ...focusedCardIds,
        ...(plannerState.evaluationCardIds ?? []),
      ]).slice(0, plannerState.settings.beamEvalCardCount),
      plannerState.settings,
      plannerState.postReviewHorizonReviews,
      { sideShown, bothSidesMode }
    );

    const refinedPlans = focusedCardIds
      .map((cardId) => {
        const basePlan = plannerState.allPlans.find((plan) => plan.cardId === cardId);
        if (!basePlan) {
          return null;
        }

        return {
          ...basePlan,
          beamValue: refinedBeamScores[cardId]?.beamValue ?? basePlan.beamValue ?? null,
          beamPNow: refinedBeamScores[cardId]?.beamPNow ?? basePlan.beamPNow ?? null,
        };
      })
      .filter(Boolean)
      .sort((left, right) => {
        const beamGap = (right.beamValue ?? -Infinity) - (left.beamValue ?? -Infinity);
        if (Math.abs(beamGap) > 1e-6) {
          return beamGap;
        }

        return right.expectedGain - left.expectedGain;
      });

    refinedPlans.forEach((plan) => {
      this.cardPlanningCache[plan.cardId] = plan;
    });
    this.rememberBeamValues(refinedPlans);
    return refinedPlans;
  }

  async evaluateTerminalSearchState(
    searchState,
    evaluationCardIds,
    settings,
    postReviewHorizonReviews,
    { sideShown = false, bothSidesMode = true } = {}
  ) {
    if (evaluationCardIds.length === 0) {
      return 0;
    }

    const horizonTime = searchState.now + settings.horizonHours * 3600;
    const horizonReviewIndex =
      searchState.reviewStep + postReviewHorizonReviews;
    const requests = evaluationCardIds.map((cardId) => ({
      key: String(cardId),
      historyRows: searchState.cardHistoriesById.get(cardId) ?? [],
      deckSummary: searchState.deckSummary,
      queryTime: horizonTime,
      queryReviewIndex: horizonReviewIndex,
      sideShown,
      bothSidesMode,
    }));
    const probabilities = await this.predictProbabilitiesForRequests(requests);

    return evaluationCardIds.reduce(
      (sum, cardId) =>
        sum +
        smoothThresholdUtility(
          probabilities[String(cardId)] ?? 0,
          settings.threshold,
          settings.utilitySharpness
        ),
      0
    );
  }

  advanceSearchState(
    searchState,
    cardId,
    wasCorrect,
    stepSeconds,
    { sideShown = false, bothSidesMode = true } = {}
  ) {
    const historyRows = searchState.cardHistoriesById.get(cardId) ?? [];
    const syntheticReview = buildSyntheticReview({
      cardId,
      historyRows,
      queryTime: searchState.now,
      queryReviewIndex: searchState.reviewStep,
      wasCorrect,
      sideShown,
      bothSidesMode,
    });
    const nextHistories = new Map(searchState.cardHistoriesById);
    nextHistories.set(cardId, [...historyRows, syntheticReview]);

    return {
      cardHistoriesById: nextHistories,
      deckSummary: extendDeckSummary(searchState.deckSummary, syntheticReview),
      now: searchState.now + stepSeconds,
      reviewStep: searchState.reviewStep + 1,
      reviewRatePerHour: searchState.reviewRatePerHour,
    };
  }

  async evaluateActionSequenceValue(
    searchState,
    candidateCardIds,
    evaluationCardIds,
    depth,
    settings,
    postReviewHorizonReviews,
    { sideShown = false, bothSidesMode = true } = {}
  ) {
    if (depth <= 0 || candidateCardIds.length === 0) {
      return this.evaluateTerminalSearchState(
        searchState,
        evaluationCardIds,
        settings,
        postReviewHorizonReviews,
        { sideShown, bothSidesMode }
      );
    }

    const probabilityMap = await this.predictProbabilitiesForSearchState(
      searchState,
      candidateCardIds,
      { sideShown, bothSidesMode }
    );
    const stepSeconds = getPlannerStepSeconds(searchState.reviewRatePerHour);
    const actionValues = await Promise.all(
      candidateCardIds.map(async (cardId) => {
        const pCorrect = clampProbability(probabilityMap[String(cardId)] ?? 0);
        const yesState = this.advanceSearchState(
          searchState,
          cardId,
          true,
          stepSeconds,
          { sideShown, bothSidesMode }
        );
        const noState = this.advanceSearchState(
          searchState,
          cardId,
          false,
          stepSeconds,
          { sideShown, bothSidesMode }
        );
        const [yesValue, noValue] = await Promise.all([
          this.evaluateActionSequenceValue(
            yesState,
            candidateCardIds,
            evaluationCardIds,
            depth - 1,
            settings,
            postReviewHorizonReviews,
            { sideShown, bothSidesMode }
          ),
          this.evaluateActionSequenceValue(
            noState,
            candidateCardIds,
            evaluationCardIds,
            depth - 1,
            settings,
            postReviewHorizonReviews,
            { sideShown, bothSidesMode }
          ),
        ]);

        return {
          cardId,
          value: pCorrect * yesValue + (1 - pCorrect) * noValue,
        };
      })
    );

    return actionValues.reduce(
      (bestValue, action) => Math.max(bestValue, action.value),
      -Infinity
    );
  }

  async scoreBeamSearchCandidates(
    searchState,
    rootCandidateCardIds,
    recursiveCandidateCardIds,
    evaluationCardIds,
    settings,
    postReviewHorizonReviews,
    { sideShown = false, bothSidesMode = true } = {}
  ) {
    if (rootCandidateCardIds.length === 0) {
      return {};
    }

    const probabilityMap = await this.predictProbabilitiesForSearchState(
      searchState,
      rootCandidateCardIds,
      { sideShown, bothSidesMode }
    );
    const stepSeconds = getPlannerStepSeconds(searchState.reviewRatePerHour);
    const scoredActions = await Promise.all(
      rootCandidateCardIds.map(async (cardId) => {
        const pCorrect = clampProbability(probabilityMap[String(cardId)] ?? 0);
        const yesState = this.advanceSearchState(
          searchState,
          cardId,
          true,
          stepSeconds,
          { sideShown, bothSidesMode }
        );
        const noState = this.advanceSearchState(
          searchState,
          cardId,
          false,
          stepSeconds,
          { sideShown, bothSidesMode }
        );
        const [yesValue, noValue] = await Promise.all([
          this.evaluateActionSequenceValue(
            yesState,
            recursiveCandidateCardIds,
            evaluationCardIds,
            settings.beamDepth - 1,
            settings,
            postReviewHorizonReviews,
            { sideShown, bothSidesMode }
          ),
          this.evaluateActionSequenceValue(
            noState,
            recursiveCandidateCardIds,
            evaluationCardIds,
            settings.beamDepth - 1,
            settings,
            postReviewHorizonReviews,
            { sideShown, bothSidesMode }
          ),
        ]);

        return {
          cardId,
          beamValue: pCorrect * yesValue + (1 - pCorrect) * noValue,
          beamPNow: pCorrect,
        };
      })
    );

    return scoredActions.reduce((mapped, action) => {
      mapped[action.cardId] = action;
      return mapped;
    }, {});
  }

  async scoreGreedyPlannerCandidates(
    searchState,
    candidateActions,
    settings,
    postReviewHorizonReviews,
    {
      sideShown = false,
      bothSidesMode = true,
      profiler = null,
      currentProbabilityMap = null,
      cachedGreedyScores = {},
    } = {}
  ) {
    if (candidateActions.length === 0) {
      return {};
    }

    const probabilityMap = currentProbabilityMap
      ? { ...currentProbabilityMap }
      : await this.predictProbabilitiesForSearchState(
          searchState,
          candidateActions,
          { sideShown, bothSidesMode }
        );
    if (!currentProbabilityMap) {
      profiler?.mark("greedy_current_probabilities", {
        candidate_actions: candidateActions.length,
      });
    }
    const stepSeconds = getPlannerStepSeconds(searchState.reviewRatePerHour);
    const horizonQueryTime = searchState.now + settings.horizonHours * 3600;
    const actionsNeedingFutureInference = candidateActions.filter(
      (action) => !cachedGreedyScores[getPlanActionKey(action.cardId, action.sideShown)]
    );
    const futureRequests = actionsNeedingFutureInference.flatMap((action) => {
      const actionKey = getPlanActionKey(action.cardId, action.sideShown);
      const yesState = this.advanceSearchState(
        searchState,
        action.cardId,
        true,
        stepSeconds,
        { sideShown: action.sideShown, bothSidesMode }
      );
      const noState = this.advanceSearchState(
        searchState,
        action.cardId,
        false,
        stepSeconds,
        { sideShown: action.sideShown, bothSidesMode }
      );
      return [
        {
          key: `${actionKey}:yes`,
          historyRows: yesState.cardHistoriesById.get(action.cardId) ?? [],
          deckSummary: yesState.deckSummary,
          queryTime: horizonQueryTime,
          queryReviewIndex: searchState.reviewStep + 1 + postReviewHorizonReviews,
          sideShown: action.sideShown,
          bothSidesMode,
        },
        {
          key: `${actionKey}:no`,
          historyRows: noState.cardHistoriesById.get(action.cardId) ?? [],
          deckSummary: noState.deckSummary,
          queryTime: horizonQueryTime,
          queryReviewIndex: searchState.reviewStep + 1 + postReviewHorizonReviews,
          sideShown: action.sideShown,
          bothSidesMode,
        },
      ];
    });
    const inferredFutureProbabilities =
      futureRequests.length > 0
        ? await this.predictProbabilitiesForRequests(futureRequests)
        : {};
    profiler?.mark("greedy_future_probabilities", {
      future_requests: futureRequests.length,
      cached_actions: candidateActions.length - actionsNeedingFutureInference.length,
    });

    return candidateActions.reduce((mapped, action) => {
      const actionKey = getPlanActionKey(action.cardId, action.sideShown);
      const cachedGreedyScore = cachedGreedyScores[actionKey];
      if (cachedGreedyScore) {
        mapped[actionKey] = cachedGreedyScore;
        return mapped;
      }
      mapped[actionKey] = {
        pCorrect: clampProbability(probabilityMap[actionKey] ?? 0),
        pIfCorrectAtHorizon: clampProbability(
          inferredFutureProbabilities[`${actionKey}:yes`] ?? 0
        ),
        pIfIncorrectAtHorizon: clampProbability(
          inferredFutureProbabilities[`${actionKey}:no`] ?? 0
        ),
        expectedHorizonProbability:
          clampProbability(probabilityMap[actionKey] ?? 0) *
            clampProbability(
              inferredFutureProbabilities[`${actionKey}:yes`] ?? 0
            ) +
          (1 - clampProbability(probabilityMap[actionKey] ?? 0)) *
            clampProbability(
              inferredFutureProbabilities[`${actionKey}:no`] ?? 0
            ),
      };
      return mapped;
    }, {});
  }

  async predictRecallCurve(
    cardId,
    delayQueries = DEFAULT_FORGETTING_CURVE_DELAY_QUERIES,
    options = {}
  ) {
    await this.readyForgettingCurve();
    const card = this.cards.find(
      (candidate) => String(candidate.card_id) === String(cardId)
    );
    if (!card) {
      throw new Error(`Card ${cardId} not found.`);
    }

    const curvesByCardId = await predictRecallCurvesForCards(
      this.forgettingCurveSession,
      this.forgettingCurveMetadata,
      [card],
      this.history,
      delayQueries,
      {
        now: options.now ?? Date.now() / 1000,
        reviewStep: options.reviewStep ?? this.lastReviewStep,
        calibration: this.forgettingCurveCalibration,
        sideShown: options.sideShown ?? false,
        bothSidesMode: options.bothSidesMode ?? true,
      }
    );

    return curvesByCardId[Number(card.card_id)] ?? [];
  }

  async printRecallCurve(
    cardId,
    delayQueries = DEFAULT_FORGETTING_CURVE_DELAY_QUERIES,
    options = {}
  ) {
    const card = this.cards.find(
      (candidate) => String(candidate.card_id) === String(cardId)
    );
    const curve = await this.predictRecallCurve(cardId, delayQueries, options);
    console.log(
      `Recall curve for ${card?.front ?? ""} (${cardId})`
    );
    console.table(
      curve.map((entry) => ({
        delay: entry.label,
        hours: Number(entry.hours.toFixed(3)),
        reviews: entry.reviews,
        pRecall: Number(entry.probability.toFixed(4)),
      }))
    );
    return curve;
  }

  printBeamSearchDiagnostics(selectedPlan) {
    const plannerState = this.lastPlannerState;
    if (!plannerState?.allPlans?.length) {
      return;
    }

    const rootActionKeys = new Set(
      (plannerState.beamRootPlans ?? []).map((plan) => plan.actionKey)
    );
    const seenPlans = (plannerState.allPlans ?? []).filter((plan) => !plan.isUnseen);

    const toDiagnosticRow = (plan, index) => {
      const oneDayDiagnostic = plan.maturityDiagnostics?.find(
        (diagnostic) => diagnostic.label === "1d"
      );
      const threeDayDiagnostic = plan.maturityDiagnostics?.find(
        (diagnostic) => diagnostic.label === "3d"
      );

      return {
        rank: index + 1,
        selected: plan.actionKey === selectedPlan?.actionKey ? "yes" : "",
        root: rootActionKeys.has(plan.actionKey) ? "yes" : "",
        cardId: plan.cardId,
        side: plan.sideLabel,
        front: plan.front,
        seenAgoMinutes: Number((plan.hoursSinceLastReview * 60).toFixed(2)),
        pNow: Number(plan.pNow.toFixed(4)),
        pBaseline1h: Number(plan.baselineHorizonProbability.toFixed(4)),
        pIfCorrect1h: Number(plan.candidates[0].pIfCorrectAtHorizon.toFixed(4)),
        pIfIncorrect1h: Number(plan.candidates[0].pIfIncorrectAtHorizon.toFixed(4)),
        pExpected1h: Number(plan.expectedHorizonProbability.toFixed(4)),
        dg1d: toRoundedNumber(oneDayDiagnostic?.expectedGain),
        dg3d: toRoundedNumber(threeDayDiagnostic?.expectedGain),
        value: toRoundedNumber(plan.expectedGain),
        suppressed: plan.explorationSuppressed ? "yes" : "",
      };
    };

    console.log("Top-5 selection pool plans");
    const rankedPlans = [...seenPlans]
      .sort((left, right) => {
        const probabilityGap = (left.pNow ?? Infinity) - (right.pNow ?? Infinity);
        if (Math.abs(probabilityGap) > 1e-6) {
          return probabilityGap;
        }
        const valueGap =
          (right.expectedGain ?? -Infinity) - (left.expectedGain ?? -Infinity);
        if (Math.abs(valueGap) > 1e-6) {
          return valueGap;
        }
        return comparePlanIdentity(left, right);
      })
      .slice(0, 5);
    console.table(
      rankedPlans.map(toDiagnosticRow)
    );

    const highMaturityRows = rankedPlans
      .filter((plan) => (plan.postReviewCurveDiagnostics?.length ?? 0) > 0)
      .map((plan) => {
        const curveValues = Object.fromEntries(
          (plan.postReviewCurveDiagnostics ?? []).map((diagnostic) => [
            `r${diagnostic.label}`,
            toRoundedNumber(diagnostic.pExpected),
          ])
        );
        return {
          card: plan.cardId,
          side: plan.sideLabel,
          text: plan.front,
          pNow: Number(plan.pNow.toFixed(4)),
          n1h: toRoundedNumber(plan.baselineHorizonProbability),
          ...curveValues,
        };
      });
    if (highMaturityRows.length > 0) {
      console.log("High-maturity post-one-review recall");
      console.table(highMaturityRows);
    }
  }

  printPlannerSummary(selectedPlan, selectionMode) {
    const plannerState = this.lastPlannerState;
    if (!plannerState) {
      return;
    }
    const selectedPlanDetails =
      plannerState.allPlans.find(
        (plan) => plan.actionKey === selectedPlan?.actionKey
      ) ?? selectedPlan;
    const seenPlans = plannerState.allPlans.filter((plan) => !plan.isUnseen);
    const eligibleSeenPlans = seenPlans.filter(
      (plan) => !plan.explorationSuppressed
    );
    const suppressedSeenPlans = seenPlans.filter(
      (plan) => plan.explorationSuppressed
    );

    const rows = [
      {
        metric: "planner horizon",
        value: `${plannerState.settings.horizonMinutes}m`,
      },
      {
        metric: "planner threshold",
        value: plannerState.settings.threshold,
      },
      {
        metric: "review rate / hour",
        value: Number(plannerState.reviewRatePerHour.toFixed(2)),
      },
      {
        metric: "median review gap (s)",
        value:
          plannerState.reviewRateStats.medianGapSeconds == null
            ? null
            : Number(plannerState.reviewRateStats.medianGapSeconds.toFixed(2)),
      },
      {
        metric: "reviews for rate",
        value: plannerState.reviewRateStats.reviewsConsidered,
      },
      {
        metric: "# seen plans",
        value: seenPlans.length,
      },
      {
        metric: "# eligible seen plans",
        value: eligibleSeenPlans.length,
      },
      {
        metric: "# suppressed seen plans",
        value: suppressedSeenPlans.length,
      },
      {
        metric: "# unseen cards",
        value: plannerState.unseenCards.length,
      },
      {
        metric: "selection mode",
        value: selectionMode,
      },
      {
        metric: "exploration mode",
        value: plannerState.explorationSettings?.enabled ? "on" : "off",
      },
      {
        metric: "# below threshold",
        value: buildWeakCardCount(
          seenPlans,
          plannerState.requestContext?.bothSidesMode,
          plannerState.settings.threshold
        ),
      },
      {
        metric: "selected card",
        value: selectedPlan?.cardId ?? null,
      },
      {
        metric: "selected side",
        value: selectedPlan?.sideLabel ?? null,
      },
      {
        metric: "selected source",
        value: selectedPlan?.isUnseen ? "unseen action" : "seen card",
      },
      {
        metric: "selected suppressed",
        value: selectedPlanDetails?.explorationSuppressed ? "yes" : "no",
      },
      {
        metric: "selected pNow",
        value: toRoundedNumber(selectedPlanDetails?.pNow),
      },
      {
        metric: "selected one-step gain",
        value: toRoundedNumber(selectedPlanDetails?.expectedGain),
      },
      {
        metric: "selected pBaseline1h",
        value: toRoundedNumber(selectedPlanDetails?.baselineHorizonProbability),
      },
      {
        metric: "selected pIfCorrect1h",
        value: toRoundedNumber(selectedPlanDetails?.candidates?.[0]?.pIfCorrectAtHorizon),
      },
      {
        metric: "selected pIfIncorrect1h",
        value: toRoundedNumber(
          selectedPlanDetails?.candidates?.[0]?.pIfIncorrectAtHorizon
        ),
      },
      {
        metric: "selected pExpected1h",
        value: toRoundedNumber(selectedPlanDetails?.expectedHorizonProbability),
      },
      {
        metric: "selected delayed gain 1d->1h",
        value: toRoundedNumber(
          selectedPlanDetails?.maturityDiagnostics?.find(
            (diagnostic) => diagnostic.label === "1d"
          )?.expectedGain
        ),
      },
      {
        metric: "selected delayed gain 3d->1h",
        value: toRoundedNumber(
          selectedPlanDetails?.maturityDiagnostics?.find(
            (diagnostic) => diagnostic.label === "3d"
          )?.expectedGain
        ),
      },
      {
        metric: "selected delayed pAttempt 1d",
        value: toRoundedNumber(
          selectedPlanDetails?.maturityDiagnostics?.find(
            (diagnostic) => diagnostic.label === "1d"
          )?.pAttempt
        ),
      },
      {
        metric: "selected delayed pAttempt 3d",
        value: toRoundedNumber(
          selectedPlanDetails?.maturityDiagnostics?.find(
            (diagnostic) => diagnostic.label === "3d"
          )?.pAttempt
        ),
      },
      {
        metric: "max searched delay",
        value: formatDelayLabel(plannerState.settings.maxDelayMinutes),
      },
    ];

    console.log("Planner summary");
    console.table(rows);
    this.printBeamSearchDiagnostics(selectedPlan);
    const selectionMethodLabel =
      selectionMode === "force_new_min_under95"
        ? "new card"
        : selectionMode === "high_gain_low_p"
          ? "high gain under p<0.96"
          : selectionMode === "minimum_probability"
            ? "min probability"
            : selectionMode === "recent_wrong"
              ? "recent wrong"
          : selectionMode;
    console.log(`Selection method: ${selectionMethodLabel}`);
  }

  printActiveSetSummary(activeCardIds, curvesByCardId, selectedCardId) {
    const rows = activeCardIds.map((cardId, index) => {
      const card = this.cards.find(
        (candidate) => String(candidate.card_id) === String(cardId)
      );
      const curve = curvesByCardId[cardId] ?? [];
      return {
        rank: index + 1,
        selected: cardId === selectedCardId ? "yes" : "",
        cardId,
        front: card?.front ?? "",
        pNow: Number(getCurveProbability(curve, "now").toFixed(4)),
        p10m: Number(getCurveProbability(curve, "10m").toFixed(4)),
      };
    });

    console.table(rows);
  }

  getUnseenCards() {
    return this.cards.filter((card) => {
      if (!isSelectableCard(card)) {
        return false;
      }
      const cardId = Number(card.card_id);
      const cardHistory = this.cardHistoriesById.get(cardId) ?? [];
      return cardHistory.length === 0;
    });
  }

  async printCurveDebugDiagnostics(reviewStep, now, selectedCardId, selectionMode) {
    const unseenCards = this.getUnseenCards();
    const deckCurves = await this.predictCurvesForCards(
      this.cards,
      [TEN_MINUTE_DELAY_QUERY, ONE_DAY_DELAY_QUERY, THREE_DAY_DELAY_QUERY],
      reviewStep,
      now
    );

    let unseenIncorrectP10m = null;
    if (unseenCards.length > 0) {
      const unseenRepresentative = unseenCards[0];
      const cardId = Number(unseenRepresentative.card_id);
      const syntheticHistory = {
        ...this.history,
        [cardId]: [buildSyntheticIncorrectReview(cardId, reviewStep, now)],
      };
      const syntheticCurves = await predictRecallCurvesForCards(
        this.forgettingCurveSession,
        this.forgettingCurveMetadata,
        [unseenRepresentative],
        syntheticHistory,
        [TEN_MINUTE_DELAY_QUERY],
        {
          now,
          reviewStep,
        }
      );
      unseenIncorrectP10m = getCurveProbability(
        syntheticCurves[cardId] ?? [],
        "10m"
      );
    }

    console.log("Curve debug summary");
    console.table([
      {
        metric: "# > 0.95 @ 10m",
        value: countCardsAboveRecallThreshold(this.cards, deckCurves, "10m"),
      },
      {
        metric: "# > 0.95 @ 1d",
        value: countCardsAboveRecallThreshold(this.cards, deckCurves, "1d"),
      },
      {
        metric: "# > 0.95 @ 3d",
        value: countCardsAboveRecallThreshold(this.cards, deckCurves, "3d"),
      },
      {
        metric: "# unseen cards",
        value: unseenCards.length,
      },
      {
        metric: "selected card",
        value: selectedCardId,
      },
      {
        metric: "selection mode",
        value: selectionMode,
      },
      {
        metric: "unseen pRecall @ 10m after incorrect",
        value:
          unseenIncorrectP10m == null
            ? null
            : Number(unseenIncorrectP10m.toFixed(4)),
      },
    ]);
  }

  async predictCurvesForCards(
    cards,
    delayQueries,
    reviewStep,
    now,
    { sideShown = false, bothSidesMode = true } = {}
  ) {
    if (cards.length === 0) {
      return {};
    }

    return predictRecallCurvesForCards(
      this.forgettingCurveSession,
      this.forgettingCurveMetadata,
      cards,
      this.history,
      delayQueries,
      {
        now,
        reviewStep,
        calibration: this.forgettingCurveCalibration,
        sideShown,
        bothSidesMode,
      }
    );
  }

  async refreshPlannerCache(
    reviewStep,
    now,
    { sideShown = false, bothSidesMode = true } = {}
  ) {
    const profiler = createTimingProfiler(
      shouldProfileSubmitPath(),
      `refreshPlannerCache review=${reviewStep}`
    );
    const settings = getPlanningSettings(getSearchParams(), this.params);
    const reviewRateStats =
      settings.reviewRatePerHour > 0
        ? {
            reviewRatePerHour: settings.reviewRatePerHour,
            reviewsConsidered: 0,
            medianGapSeconds: null,
            startTime: null,
            endTime: null,
          }
        : estimateReviewRatePerHour(this.allReviewsChronological);
    const reviewRatePerHour = reviewRateStats.reviewRatePerHour;
    const postReviewHorizonReviews = estimateInterveningReviews(
      settings.horizonHours,
      reviewRatePerHour
    );

    const seenCards = this.cards.filter((card) => {
      if (!isSelectableCard(card)) {
        return false;
      }
      const cardId = Number(card.card_id);
      const cardHistory = this.cardHistoriesById.get(cardId) ?? [];
      return cardHistory.length > 0;
    });
    const unseenCards = this.getUnseenCards();
    const seenActions = buildPlanningActions(seenCards, bothSidesMode, sideShown);
    const actionContextByKey = seenActions.reduce((mapped, action) => {
      const actionKey = getPlanActionKey(action.cardId, action.sideShown);
      const historyRows = this.cardHistoriesById.get(action.cardId) ?? [];
      const cachedPlan = this.cardPlanningCache[actionKey] ?? null;
      mapped[actionKey] = {
        action,
        actionKey,
        historyRows,
        cachedPlan,
        shouldReuseCachedPlan: shouldReuseStableCardPlan({
          reviewStep,
          cardId: action.cardId,
          historyRows,
          cachedPlan,
        }),
      };
      return mapped;
    }, {});
    const inferenceActions = seenActions.filter((action) => {
      const actionKey = getPlanActionKey(action.cardId, action.sideShown);
      return !actionContextByKey[actionKey]?.shouldReuseCachedPlan;
    });
    const cachedActions = seenActions.filter((action) => {
      const actionKey = getPlanActionKey(action.cardId, action.sideShown);
      return !!actionContextByKey[actionKey]?.shouldReuseCachedPlan;
    });
    profiler.mark("planning_actions_built", {
      seen_cards: seenCards.length,
      unseen_cards: unseenCards.length,
      seen_actions: seenActions.length,
      inferred_actions: inferenceActions.length,
      cached_actions: cachedActions.length,
    });
    const currentRequests = inferenceActions.map((action) => {
      const actionKey = getPlanActionKey(action.cardId, action.sideShown);
      const historyRows = actionContextByKey[actionKey]?.historyRows ?? [];
      const sourceId =
        historyRows.length > 0
          ? Number(historyRows[historyRows.length - 1].source_id ?? 0)
          : null;
      const deckSummary =
        sourceId == null
          ? this.deckSummaryIndex.allSummary
          : this.deckSummaryIndex.summariesBySource.get(sourceId) ??
            this.deckSummaryIndex.allSummary;

      return {
        key: getPlanActionKey(action.cardId, action.sideShown),
        historyRows,
        deckSummary,
        queryTime: now,
        queryReviewIndex: reviewStep,
        sideShown: action.sideShown,
        bothSidesMode,
      };
    });
    const currentProbabilities = await this.predictProbabilitiesForRequests(
      currentRequests
    );
    cachedActions.forEach((action) => {
      const actionKey = getPlanActionKey(action.cardId, action.sideShown);
      const cachedPlan = actionContextByKey[actionKey]?.cachedPlan;
      if (cachedPlan) {
        currentProbabilities[actionKey] = clampProbability(cachedPlan.pNow ?? 0);
      }
    });
    profiler.mark("current_probabilities", {
      current_requests: currentRequests.length,
      cached_requests: cachedActions.length,
    });
    const futureInferenceThreshold = getFutureInferenceThreshold(settings);
    const horizonInferenceActions = inferenceActions.filter((action) => {
      const actionKey = getPlanActionKey(action.cardId, action.sideShown);
      return (
        Number(currentProbabilities[actionKey] ?? 0) < futureInferenceThreshold
      );
    });
    const futureProbabilityRequests = horizonInferenceActions.map((action) => {
      const actionKey = getPlanActionKey(action.cardId, action.sideShown);
      const historyRows = actionContextByKey[actionKey]?.historyRows ?? [];
      const sourceId =
        historyRows.length > 0
          ? Number(historyRows[historyRows.length - 1].source_id ?? 0)
          : null;
      const deckSummary =
        sourceId == null
          ? this.deckSummaryIndex.allSummary
          : this.deckSummaryIndex.summariesBySource.get(sourceId) ??
            this.deckSummaryIndex.allSummary;

      return {
        key: `${actionKey}:${ONE_HOUR_DELAY_QUERY.label}`,
        historyRows,
        deckSummary,
        queryTime: now + settings.horizonHours * 3600,
        queryReviewIndex: reviewStep + postReviewHorizonReviews,
        sideShown: action.sideShown,
        bothSidesMode,
      };
    });
    const futureProbabilities = await this.predictProbabilitiesForRequests(
      futureProbabilityRequests
    );
    cachedActions.forEach((action) => {
      const actionKey = getPlanActionKey(action.cardId, action.sideShown);
      const cachedPlan = actionContextByKey[actionKey]?.cachedPlan;
      if (!cachedPlan) {
        return;
      }
      futureProbabilities[`${actionKey}:${ONE_HOUR_DELAY_QUERY.label}`] =
        clampProbability(cachedPlan.pOneHour ?? cachedPlan.pNow ?? 0);
    });
    seenActions.forEach((action) => {
      const actionKey = getPlanActionKey(action.cardId, action.sideShown);
      if (
        futureProbabilities[`${actionKey}:${ONE_HOUR_DELAY_QUERY.label}`] ===
        undefined
      ) {
        futureProbabilities[`${actionKey}:${ONE_HOUR_DELAY_QUERY.label}`] =
          clampProbability(currentProbabilities[actionKey] ?? 0);
      }
    });
    profiler.mark("future_probabilities", {
      future_requests: futureProbabilityRequests.length,
      cached_requests: cachedActions.length,
      flat_requests: seenActions.length - horizonInferenceActions.length,
    });
    const searchState = {
      cardHistoriesById: this.cardHistoriesById,
      deckSummary: this.deckSummaryIndex.allSummary,
      now,
      reviewStep,
      reviewRatePerHour,
    };
    const greedyScores = await this.scoreGreedyPlannerCandidates(
      searchState,
      seenActions,
      settings,
      postReviewHorizonReviews,
      {
        sideShown,
        bothSidesMode,
        profiler,
        currentProbabilityMap: currentProbabilities,
        cachedGreedyScores: seenActions.reduce((mapped, action) => {
          const actionKey = getPlanActionKey(action.cardId, action.sideShown);
          const cachedGreedyScore =
            Number(currentProbabilities[actionKey] ?? 0) >=
            futureInferenceThreshold
              ? getFlatGreedyScoreFromProbability(currentProbabilities[actionKey])
              : getCachedGreedyScoreFromPlan(
                  actionContextByKey[actionKey]?.cachedPlan
                );
          if (cachedGreedyScore) {
            mapped[actionKey] = cachedGreedyScore;
          }
          return mapped;
        }, {}),
      }
    );
    profiler.mark("greedy_scores_complete", {
      greedy_scores: Object.keys(greedyScores).length,
    });

    const maturityDiagnosticRequests = seenActions.flatMap((action) => {
      const cardId = Number(action.cardId);
      const historyRows = this.cardHistoriesById.get(cardId) ?? [];
      if (!shouldUseDelayedReviewGainCandidates(historyRows, now)) {
        return [];
      }

      const sourceId =
        historyRows.length > 0
          ? getSourceId(historyRows[historyRows.length - 1])
          : null;
      const sourceReviews =
        sourceId == null
          ? this.allReviewsChronological
          : this.deckSummaryIndex.rowsBySource.get(sourceId) ??
            this.allReviewsChronological;
      const deckSummary =
        sourceId == null
          ? this.deckSummaryIndex.allSummary
          : this.deckSummaryIndex.summariesBySource.get(sourceId) ??
            this.deckSummaryIndex.allSummary;
      const reviewRatePerHour =
        estimateAverageReviewsPerDay(sourceReviews, now) / 24;
      const actionKey = getPlanActionKey(cardId, action.sideShown);

      return DELAYED_GAIN_CANDIDATE_DELAYS_MINUTES.flatMap((minutes) => {
        const label = formatDelayLabel(minutes);
        const delayHours = minutes / 60;
        const attemptReviewOffset = estimateInterveningReviews(
          delayHours,
          reviewRatePerHour
        );
        const postReviewOffset = estimateInterveningReviews(
          DELAYED_GAIN_POST_REVIEW_HORIZON_HOURS,
          reviewRatePerHour
        );
        const reviewTime = now + delayHours * 3600;
        const reviewIndex = reviewStep + attemptReviewOffset;
        const horizonQueryTime =
          reviewTime + DELAYED_GAIN_POST_REVIEW_HORIZON_HOURS * 3600;
        const baselineReviewIndex = reviewIndex + postReviewOffset;
        const horizonReviewIndex = reviewIndex + 1 + postReviewOffset;
        const yesReview = buildSyntheticReview({
          cardId,
          historyRows,
          queryTime: reviewTime,
          queryReviewIndex: reviewIndex,
          wasCorrect: true,
          sideShown: action.sideShown,
          bothSidesMode,
        });
        const noReview = buildSyntheticReview({
          cardId,
          historyRows,
          queryTime: reviewTime,
          queryReviewIndex: reviewIndex,
          wasCorrect: false,
          sideShown: action.sideShown,
          bothSidesMode,
        });

        return [
          {
            key: `${actionKey}:${label}:attempt`,
            historyRows,
            deckSummary,
            queryTime: reviewTime,
            queryReviewIndex: reviewIndex,
            sideShown: action.sideShown,
            bothSidesMode,
          },
          {
            key: `${actionKey}:${label}:baseline`,
            historyRows,
            deckSummary,
            queryTime: horizonQueryTime,
            queryReviewIndex: baselineReviewIndex,
            sideShown: action.sideShown,
            bothSidesMode,
          },
          {
            key: `${actionKey}:${label}:yes`,
            historyRows: [...historyRows, yesReview],
            deckSummary: extendDeckSummary(deckSummary, yesReview),
            queryTime: horizonQueryTime,
            queryReviewIndex: horizonReviewIndex,
            sideShown: action.sideShown,
            bothSidesMode,
          },
          {
            key: `${actionKey}:${label}:no`,
            historyRows: [...historyRows, noReview],
            deckSummary: extendDeckSummary(deckSummary, noReview),
            queryTime: horizonQueryTime,
            queryReviewIndex: horizonReviewIndex,
            sideShown: action.sideShown,
            bothSidesMode,
          },
        ];
      });
    });
    const maturityDiagnosticProbabilities =
      maturityDiagnosticRequests.length > 0
        ? await this.predictProbabilitiesForRequests(maturityDiagnosticRequests)
        : {};
    const maturityDiagnosticsByActionKey = seenActions.reduce((mapped, action) => {
      const cardId = Number(action.cardId);
      const historyRows = this.cardHistoriesById.get(cardId) ?? [];
      if (!shouldUseDelayedReviewGainCandidates(historyRows, now)) {
        return mapped;
      }

      const sourceId =
        historyRows.length > 0
          ? getSourceId(historyRows[historyRows.length - 1])
          : null;
      const sourceReviews =
        sourceId == null
          ? this.allReviewsChronological
          : this.deckSummaryIndex.rowsBySource.get(sourceId) ??
            this.allReviewsChronological;
      const averageReviewsPerDay = estimateAverageReviewsPerDay(
        sourceReviews,
        now
      );
      const actionKey = getPlanActionKey(cardId, action.sideShown);
      const diagnostics = DELAYED_GAIN_CANDIDATE_DELAYS_MINUTES.map((minutes) => {
        const label = formatDelayLabel(minutes);
        const requestKeyBase = `${actionKey}:${label}`;
        const pAttempt = clampProbability(
          maturityDiagnosticProbabilities[`${requestKeyBase}:attempt`] ?? 0
        );
        const baselineHorizonProbability = clampProbability(
          maturityDiagnosticProbabilities[`${requestKeyBase}:baseline`] ?? pAttempt
        );
        const pIfCorrectAtHorizon = clampProbability(
          maturityDiagnosticProbabilities[`${requestKeyBase}:yes`] ??
            baselineHorizonProbability
        );
        const pIfIncorrectAtHorizon = clampProbability(
          maturityDiagnosticProbabilities[`${requestKeyBase}:no`] ??
            baselineHorizonProbability
        );
        const expectedHorizonProbability =
          pAttempt * pIfCorrectAtHorizon +
          (1 - pAttempt) * pIfIncorrectAtHorizon;

        return {
          label,
          minutes,
          reviewRatePerDay: averageReviewsPerDay,
          pAttempt,
          baselineHorizonProbability,
          pIfCorrectAtHorizon,
          pIfIncorrectAtHorizon,
          expectedHorizonProbability,
          expectedGain:
            expectedHorizonProbability - baselineHorizonProbability,
        };
      });
      mapped[actionKey] = diagnostics;
      return mapped;
    }, {});
    const postReviewCurveDiagnosticRequests = seenActions.flatMap((action) => {
      const cardId = Number(action.cardId);
      const historyRows = this.cardHistoriesById.get(cardId) ?? [];
      if (!shouldUseDelayedReviewGainCandidates(historyRows, now)) {
        return [];
      }

      const sourceId =
        historyRows.length > 0
          ? getSourceId(historyRows[historyRows.length - 1])
          : null;
      const sourceReviews =
        sourceId == null
          ? this.allReviewsChronological
          : this.deckSummaryIndex.rowsBySource.get(sourceId) ??
            this.allReviewsChronological;
      const deckSummary =
        sourceId == null
          ? this.deckSummaryIndex.allSummary
          : this.deckSummaryIndex.summariesBySource.get(sourceId) ??
            this.deckSummaryIndex.allSummary;
      const reviewRatePerHour =
        estimateAverageReviewsPerDay(sourceReviews, now) / 24;
      const actionKey = getPlanActionKey(cardId, action.sideShown);
      const yesReview = buildSyntheticReview({
        cardId,
        historyRows,
        queryTime: now,
        queryReviewIndex: reviewStep,
        wasCorrect: true,
        sideShown: action.sideShown,
        bothSidesMode,
      });
      const noReview = buildSyntheticReview({
        cardId,
        historyRows,
        queryTime: now,
        queryReviewIndex: reviewStep,
        wasCorrect: false,
        sideShown: action.sideShown,
        bothSidesMode,
      });

      return HIGH_MATURITY_POST_REVIEW_DELAY_QUERIES.flatMap((delayQuery) => {
        const postReviewOffset = estimateInterveningReviews(
          delayQuery.hours,
          reviewRatePerHour
        );
        const horizonReviewIndex = reviewStep + 1 + postReviewOffset;
        const queryTime = now + delayQuery.hours * 3600;
        return [
          {
            key: `${actionKey}:post-review:${delayQuery.label}:yes`,
            historyRows: [...historyRows, yesReview],
            deckSummary: extendDeckSummary(deckSummary, yesReview),
            queryTime,
            queryReviewIndex: horizonReviewIndex,
            sideShown: action.sideShown,
            bothSidesMode,
          },
          {
            key: `${actionKey}:post-review:${delayQuery.label}:no`,
            historyRows: [...historyRows, noReview],
            deckSummary: extendDeckSummary(deckSummary, noReview),
            queryTime,
            queryReviewIndex: horizonReviewIndex,
            sideShown: action.sideShown,
            bothSidesMode,
          },
        ];
      });
    });
    const postReviewCurveDiagnosticProbabilities =
      postReviewCurveDiagnosticRequests.length > 0
        ? await this.predictProbabilitiesForRequests(postReviewCurveDiagnosticRequests)
        : {};
    const postReviewCurvesByActionKey = seenActions.reduce((mapped, action) => {
      const cardId = Number(action.cardId);
      const historyRows = this.cardHistoriesById.get(cardId) ?? [];
      if (!shouldUseDelayedReviewGainCandidates(historyRows, now)) {
        return mapped;
      }

      const actionKey = getPlanActionKey(cardId, action.sideShown);
      const pNow = clampProbability(currentProbabilities[actionKey] ?? 0);
      mapped[actionKey] = HIGH_MATURITY_POST_REVIEW_DELAY_QUERIES.map(
        (delayQuery) => {
          const yesProbability = clampProbability(
            postReviewCurveDiagnosticProbabilities[
              `${actionKey}:post-review:${delayQuery.label}:yes`
            ] ?? 0
          );
          const noProbability = clampProbability(
            postReviewCurveDiagnosticProbabilities[
              `${actionKey}:post-review:${delayQuery.label}:no`
            ] ?? 0
          );
          return {
            label: delayQuery.label,
            hours: delayQuery.hours,
            pIfCorrect: yesProbability,
            pIfIncorrect: noProbability,
            pExpected: pNow * yesProbability + (1 - pNow) * noProbability,
          };
        }
      );
      return mapped;
    }, {});

    const cardsById = new Map(
      this.cards.map((card) => [Number(card.card_id), card])
    );
    const plans = seenActions.map((action) => {
      const cardId = Number(action.cardId);
      const card = cardsById.get(cardId) ?? {};
      const historyRows = this.cardHistoriesById.get(cardId) ?? [];
      const lastReview = historyRows[historyRows.length - 1];
      const actionKey = getPlanActionKey(cardId, action.sideShown);
      const pNow = currentProbabilities[actionKey] ?? 0;
      const baselineHorizonProbability =
        futureProbabilities[`${actionKey}:${ONE_HOUR_DELAY_QUERY.label}`] ?? pNow;
      const greedyScore = greedyScores[actionKey] ?? null;
      const expectedHorizonProbability =
        greedyScore?.expectedHorizonProbability ?? baselineHorizonProbability;
      const expectedGain =
        expectedHorizonProbability - baselineHorizonProbability;
      const hoursSinceLastReview = lastReview
        ? Math.max(now - getSubmittedTime(lastReview), 0) / 3600
        : 0;

      return {
        actionKey,
        cardId,
        sideShown: !!action.sideShown,
        sideLabel: formatPlanSide(action.sideShown),
        front: card.front ?? "",
        pNow,
        pTenMinutes: pNow,
        pOneHour:
          futureProbabilities[`${actionKey}:${ONE_HOUR_DELAY_QUERY.label}`] ?? pNow,
        pOneDay: pNow,
        pThreeDays: pNow,
        bestDelayMinutes: 0,
        bestDelayLabel: "now",
        expectedHorizonProbability,
        expectedGain,
        pAttemptAtBest: greedyScore?.pCorrect ?? pNow,
        baselineHorizonProbability,
        dueAt: now,
        dueInMinutes: 0,
        scheduled: expectedGain > 1e-6,
        dueNow: expectedGain > 1e-6,
        hoursSinceLastReview,
        maturityDiagnostics: maturityDiagnosticsByActionKey[actionKey] ?? [],
        postReviewCurveDiagnostics: postReviewCurvesByActionKey[actionKey] ?? [],
        candidates: [
          {
            label: "1h",
            minutes: settings.horizonMinutes,
            reviews: postReviewHorizonReviews,
            pAttempt: greedyScore?.pCorrect ?? pNow,
            baselineHorizonProbability,
            pIfCorrectAtHorizon:
              greedyScore?.pIfCorrectAtHorizon ?? baselineHorizonProbability,
            pIfIncorrectAtHorizon:
              greedyScore?.pIfIncorrectAtHorizon ?? baselineHorizonProbability,
            expectedHorizonProbability,
            expectedGain,
          },
        ],
        beamValue: expectedGain,
        beamPNow: greedyScore?.pCorrect ?? pNow,
      };
    });
    const explorationSettings = getExplorationSettings();
    const explorationPlans = explorationSettings.enabled
      ? applyExplorationSuppressionToPlans(
          // Exploration mode only suppresses cards with real history; unseen
          // cards remain available via the new-card path.
          plans,
          (this.explorationDelayByCardId = syncExplorationDelayByCardId({
            seenPlans: plans,
            previousDelayByCardId: this.explorationDelayByCardId,
            now,
          })),
          now
        )
      : plans;
    if (!explorationSettings.enabled) {
      this.explorationDelayByCardId = {};
    }

    let unseenNowProbability = 0;
    if (unseenCards.length > 0) {
      const unseenCardId = Number(unseenCards[0].card_id);
      const unseenProbabilities = await this.predictProbabilitiesForRequests([
        {
          key: String(unseenCardId),
          historyRows: [],
          deckSummary: this.deckSummaryIndex.allSummary,
          queryTime: now,
          queryReviewIndex: reviewStep,
          sideShown,
          bothSidesMode,
        },
      ]);
      unseenNowProbability =
        unseenProbabilities[String(unseenCardId)] ?? 0;
      profiler.mark("unseen_probability", {
        unseen_card_id: unseenCardId,
      });
    }

    const sortedPlans = sortDuePlans(explorationPlans);
    const beamCandidateCardIds = sortedPlans
      .slice(0, settings.beamRootWidth)
      .map((plan) => plan.actionKey);
    const beamRootPlans = sortedPlans.slice(0, settings.beamRootWidth);

    this.cardPlanningCache = sortedPlans.reduce((mapped, plan) => {
      mapped[plan.actionKey] = plan;
      return mapped;
    }, {});
    this.rememberBeamValues(sortedPlans);
    this.lastPlannerState = {
      settings,
      reviewRatePerHour,
      reviewRateStats,
      postReviewHorizonReviews,
      searchState,
      allPlans: sortedPlans,
      sidelinedCardIds: new Set(this.sessionSidelinedCardIds),
      unseenCards,
      unseenNowProbability,
      beamCandidateCardIds,
      recursiveCandidateCardIds: [],
      evaluationCardIds: beamCandidateCardIds,
      beamRootPlans,
      beamRefinement: null,
      newCardGate: null,
      requestContext: { sideShown, bothSidesMode },
      explorationSettings,
    };
    profiler.mark("planner_state_built", {
      sorted_plans: sortedPlans.length,
    });
    profiler.flush({
      reviewStep,
      seenCards: seenCards.length,
      seenActions: seenActions.length,
      bothSidesMode,
    });
    return this.lastPlannerState;
  }

  buildFsrsPlan(card, now, sideShown, bothSidesMode) {
    return this.materializeFsrsPlan(
      this.buildFsrsStaticPlan(card, sideShown, bothSidesMode),
      now
    );
  }

  disperseFsrsSiblingPlans(plans, now) {
    return this.applyFsrsSiblingDispersionToPlans(plans).map((plan) =>
      this.materializeFsrsPlan(plan, now)
    );
  }

  logFsrsStepDiagnostics({
    belowThresholdSeenPlans,
    dueSeenPlans,
    selectedPlan,
    freshUnseenCount = 0,
    now,
  }) {
    console.log(
      `[fsrs] card=${selectedPlan?.cardId ?? "unknown"} side=${
        selectedPlan?.sideShown ? "back" : "front"
      } due=${dueSeenPlans.length} fresh_unseen=${freshUnseenCount} below_target=${belowThresholdSeenPlans.length} p_success(card)=${Number(
        selectedPlan?.pNow ?? 0
      ).toFixed(4)} phase=${selectedPlan?.phase ?? "unknown"} step_index=${
        selectedPlan?.stepIndex ?? "null"
      } reason=${
        selectedPlan?.schedulingReason ?? "unknown"
      } due_in_mins=${Number(selectedPlan?.dueInMinutes ?? 0).toFixed(2)} due_now=${
        Number(selectedPlan?.dueAt ?? Number.POSITIVE_INFINITY) <= Number(now)
      } now_mod_1e3=${Number(now % 1000).toFixed(3)}`
    );
  }

  selectFsrsPlan({ reviewStep, avoidCard, sideShown = false, bothSidesMode = true }) {
    const now = Date.now() / 1000;
    const cache = this.ensureFsrsScheduleCache({ sideShown, bothSidesMode });
    const seenPlans = cache.seenActionKeys
      .map((actionKey) => cache.plansByActionKey.get(actionKey))
      .filter(Boolean)
      .map((plan) => this.materializeFsrsPlan(plan, now));
    const unseenPlans = cache.unseenActionKeys
      .map((actionKey) => cache.plansByActionKey.get(actionKey))
      .filter(Boolean)
      .map((plan) => this.materializeFsrsPlan(plan, now));

    const sortPlans = (plans) =>
      [...plans].sort((left, right) => {
        const dueGap =
          Number(left.dueAt ?? Number.POSITIVE_INFINITY) -
          Number(right.dueAt ?? Number.POSITIVE_INFINITY);
        if (Math.abs(dueGap) > 1e-6) {
          return dueGap;
        }
        const probabilityGap = Number(left.pNow) - Number(right.pNow);
        if (Math.abs(probabilityGap) > 1e-6) {
          return probabilityGap;
        }
        return Number(left.cardId) - Number(right.cardId);
      });
    const eligibleSeenPlans = seenPlans.filter(
      (plan) => String(plan.cardId) !== String(avoidCard)
    );
    const seenPlansForSelection =
      eligibleSeenPlans.length > 0 ? eligibleSeenPlans : seenPlans;
    const belowThresholdSeenPlans = seenPlansForSelection.filter(
      (plan) => Number(plan.pNow) <= this.plannerTarget
    );
    const dueSeenPlans = seenPlansForSelection.filter(
      (plan) => Number(plan.dueAt ?? Number.POSITIVE_INFINITY) <= now
    );
    const sortedDueSeenPlans = sortPlans(dueSeenPlans);
    const sortedSeenPlans = sortPlans(seenPlansForSelection);
    const eligibleUnseenPlans = unseenPlans.filter(
      (plan) => String(plan.cardId) !== String(avoidCard)
    );
    const dueDelayedSiblingPlans = sortPlans(
      eligibleUnseenPlans.filter(
        (plan) =>
          plan.schedulingReason === "new_sibling_delayed_one_hour" &&
          Number(plan.dueAt ?? Number.POSITIVE_INFINITY) <= now
      )
    );
    const freshUnseenPlans = eligibleUnseenPlans.filter(
      (plan) =>
        (this.cardHistoriesById.get(Number(plan.cardId))?.length ?? 0) === 0
    );
    const fallbackFreshUnseenPlans = unseenPlans.filter(
      (plan) =>
        String(plan.cardId) !== String(avoidCard) &&
        (this.cardHistoriesById.get(Number(plan.cardId))?.length ?? 0) === 0
    );
    const sortedUnseenPlans = sortPlans(
      eligibleUnseenPlans.length > 0 ? eligibleUnseenPlans : unseenPlans
    );
    const sortedFreshUnseenPlans = sortPlans(
      freshUnseenPlans.length > 0 ? freshUnseenPlans : fallbackFreshUnseenPlans
    );
    const randomFreshUnseenPlan =
      this.params.useRandomOrder === false
        ? null
        : chooseRandomPlan(sortedFreshUnseenPlans, avoidCard);

    let selectedPlan =
      sortedDueSeenPlans[0] ??
      dueDelayedSiblingPlans[0] ??
      randomFreshUnseenPlan ??
      sortedFreshUnseenPlans[0] ??
      null;
    let probability = selectedPlan?.pNow ?? 0;
    if (selectedPlan) {
      this.lastSelection = {
        ...selectedPlan,
        selectionMode: "fsrs",
      };
      this.lastReviewStep = reviewStep;
      this.logFsrsStepDiagnostics({
        belowThresholdSeenPlans,
        dueSeenPlans,
        selectedPlan,
        freshUnseenCount: sortedFreshUnseenPlans.length,
        now,
      });
      return {
        probability,
        cardId: selectedPlan.cardId,
        sideShown: selectedPlan.sideShown,
      };
    }

    selectedPlan = sortedSeenPlans[0] ?? sortedUnseenPlans[0] ?? null;
    if (!selectedPlan?.cardId) {
      throw new Error("No cards are available for selection.");
    }
    probability = selectedPlan.pNow ?? 0;
    this.lastSelection = {
      ...selectedPlan,
      selectionMode: "fsrs",
    };
    this.lastReviewStep = reviewStep;
    this.logFsrsStepDiagnostics({
      belowThresholdSeenPlans,
      dueSeenPlans,
      selectedPlan,
      freshUnseenCount: sortedFreshUnseenPlans.length,
      now,
    });
    return {
      probability,
      cardId: selectedPlan.cardId,
      sideShown: selectedPlan.sideShown,
    };
  }

  async selectCard(reviewStep, avoidCard, sideShown = false, bothSidesMode = true) {
    if (this.schedulerModel === "FSRS") {
      return this.selectFsrsPlan({
        reviewStep,
        avoidCard,
        sideShown,
        bothSidesMode,
      });
    }

    await this.readyForgettingCurve();
    this.lastReviewStep = reviewStep;
    const now = Date.now() / 1000;
    const plannerState = await this.refreshPlannerCache(reviewStep, now, {
      sideShown,
      bothSidesMode,
    });

    const unseenCardIds = plannerState.unseenCards.map((card) =>
      Number(card.card_id)
    );
    const seenPlans = (plannerState.allPlans ?? []).filter((plan) => !plan.isUnseen);
    const nonSidelinedSeenPlans = filterSessionSidelinedPlans(
      seenPlans,
      this.sessionSidelinedCardIds
    );
    const effectiveSeenPlans =
      nonSidelinedSeenPlans.length > 0 ? nonSidelinedSeenPlans : seenPlans;
    const eligibleSeenPlans = effectiveSeenPlans.filter(
      (plan) => !plan.explorationSuppressed
    );

    let selectedPlan = null;
    let selectionMode = "beam";
    let probability = 0;
    let beamRefinement = {
      triggered: false,
      superseded: false,
      originalSelectedCardId: null,
      originalSelectedBeamValue: null,
      topCachedCardId: null,
      topCachedBeamValue: null,
      finalSelectedCardId: null,
      comparisonSummary: null,
    };

    const seenPlansForSelection =
      eligibleSeenPlans.length > 0 ? eligibleSeenPlans : effectiveSeenPlans;
    const selectableSeenPlans = seenPlansForSelection.filter(
      (plan) => String(plan.cardId) !== String(avoidCard)
    );
    const seenPlansInMix =
      selectableSeenPlans.length > 0 ? selectableSeenPlans : seenPlansForSelection;
    const under95Count = buildWeakCardCount(seenPlansInMix, bothSidesMode);
    const shouldForceNewCard =
      unseenCardIds.length > 0 &&
      under95Count < plannerState.settings.minUnder95Count;
    const weightedSeenChoice =
      seenPlansForSelection.length > 0
        ? chooseWeightedSeenPlan({
            plans: seenPlansForSelection,
            avoidCard,
            cardHistoriesById: this.cardHistoriesById,
            explorationModeEnabled: plannerState.explorationSettings?.enabled,
          })
        : null;

    if (shouldForceNewCard) {
      const selectedCardId = chooseRandomCardId(unseenCardIds, avoidCard);
      const selectedSideShown = chooseNewCardSideShown(
        bothSidesMode,
        sideShown
      );
      selectedPlan = {
        actionKey: getPlanActionKey(selectedCardId, selectedSideShown),
        cardId: selectedCardId,
        sideShown: selectedSideShown,
        sideLabel: formatPlanSide(selectedSideShown),
        isUnseen: true,
        pNow: plannerState.unseenNowProbability,
      };
      selectionMode = "force_new_min_under95";
      probability = plannerState.unseenNowProbability;
    } else if (weightedSeenChoice?.plan) {
      selectedPlan = weightedSeenChoice.plan;
      selectionMode = weightedSeenChoice.selectionMode;
      probability = weightedSeenChoice.plan.pNow;
    } else {
      const greedyPlan =
        sortDuePlans(seenPlansForSelection).find(
          (plan) => String(plan.cardId) !== String(avoidCard)
        ) ?? sortDuePlans(seenPlansForSelection)[0];
      if (greedyPlan) {
        selectedPlan = greedyPlan;
        selectionMode = "greedy";
        probability = greedyPlan.pNow;
      } else if (unseenCardIds.length > 0) {
        const selectedCardId = chooseRandomCardId(unseenCardIds, avoidCard);
        const selectedSideShown = chooseNewCardSideShown(
          bothSidesMode,
          sideShown
        );
        selectedPlan = {
          actionKey: getPlanActionKey(selectedCardId, selectedSideShown),
          cardId: selectedCardId,
          sideShown: selectedSideShown,
          sideLabel: formatPlanSide(selectedSideShown),
          isUnseen: true,
          pNow: plannerState.unseenNowProbability,
        };
        selectionMode = "new_card_fallback";
        probability = plannerState.unseenNowProbability;
      }
    }

    if (selectedPlan?.cardId == null) {
      throw new Error("No cards are available for selection.");
    }

    delete this.lastBeamValueByCardId[selectedPlan.cardId];
    plannerState.beamRefinement = beamRefinement;
    this.lastSelection = {
      ...selectedPlan,
      selectionMode,
    };

    this.printPlannerSummary(selectedPlan, selectionMode);
    if (this.shouldDebugCurve()) {
      await this.printCurveDebugDiagnostics(
        reviewStep,
        now,
        selectedPlan.cardId,
        selectionMode
      );
      await this.printRecallCurve(selectedPlan.cardId, undefined, {
        sideShown: selectedPlan.sideShown,
        bothSidesMode,
      });
    }

    return {
      probability,
      cardId: selectedPlan.cardId,
      sideShown: selectedPlan.sideShown,
    };
  }
}

export default LSTM;
